// Icons
import {
  AlertCircle,
  ChevronLeft,
  FileText,
  MapPin,
  Package,
  Plus,
  RefreshCw,
  Save,
  Trash2,
  Upload,
  User,
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';

// UI Components
import { AsyncSearchableSelect } from '@/components/ui/async-searchable-select';
// Services
import AttachmentUploader from '@/components/awb/AttachmentUploader';
import { KYCDocument } from '@/components/awb/KYCDocumentsTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import SearchableSelect from '@/components/ui/searchable-select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/useToast';
import awbService from '@/services/awbService';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

// ============================================================================
// TYPES (Same as AWBCreatePageNew)
// ============================================================================

interface MasterDataOption {
  _id: string;
  name: string;
  code: string;
  symbol?: string;
  dialCode?: string;
}

interface FreeFormNoteOption {
  _id: string;
  name: string;
  srNo: string;
  comment?: string;
  maxShipmentValue?: number;
}

interface MasterData {
  customers: MasterDataOption[];
  companies: MasterDataOption[];
  countries: MasterDataOption[];
  products: MasterDataOption[];
  services: MasterDataOption[];
  currencies: MasterDataOption[];
  vendors: MasterDataOption[];
  zones: MasterDataOption[];
  incoterms: MasterDataOption[];
  hubs: MasterDataOption[];
  licenseLocations: MasterDataOption[];
  freeFormNotes: FreeFormNoteOption[];
  isLoading: boolean;
}

const GST_TYPES = [
  { value: 'GST', label: 'GST' },
  { value: 'IGST', label: 'IGST' },
  { value: 'CGST_SGST', label: 'CGST + SGST' },
  { value: 'EXEMPT', label: 'Exempt' },
];

interface WeightDimension {
  parcelNo?: string;
  boxNo: number;
  actualWeight: number;
  length: number;
  breadth: number;
  height: number;
  volumetricWeight: number;
  chargeableWeight: number;
}

interface ShipperConsigneeData {
  code: string;
  company: string;
  personName: string;
  address1: string;
  address2: string;
  address3: string;
  postZipCode: string;
  city: string;
  stateCounty: string;
  country: string;
  countryCode: string;
  phoneNumber: string;
  phoneNumber2?: string;
  emailAddress: string;
  kycType: string;
  kycNumber: string;
  kycDocuments: KYCDocument[];
  kycUpload?: File | null;
  saveToAddressBook: boolean;
  updateAddressBook: boolean;
}

interface InvoiceLineItem {
  boxNo: string;
  srNo: number;
  description: string;
  hsCode: string;
  unitType: string;
  quantity: number;
  unitWeight: number;
  igst: number;
  unitRate: number;
  amount: number;
}

interface ShipmentInvoice {
  invoiceType: string;
  currency: string;
  incoterms: string;
  note: string;
  remarks: string;
  lineItems: InvoiceLineItem[];
}

interface SurchargeLine {
  enabled: boolean;
  amount: number;
  quantity: number;
}

interface PaymentReceiptLine {
  paymentNo: string;
  paymentDate: string;
  accountNo: string;
  modeOfPayment: string;
  particulars: string;
  amount: number;
}

interface SalesBilling {
  salesCurrency: string;
  gstType: string;
  gstApplicable: boolean;
  gstApplicableEditable: boolean;
  freight: number;
  freightEditable: boolean;
  freightPerKg: number;
  searchCharge: string;
  addressCorrection: SurchargeLine;
  clearanceCharge: SurchargeLine;
  elevatedRiskSurcharge: SurchargeLine;
  ess: SurchargeLine;
  imitation: SurchargeLine;
  medicineHandlingCharges: SurchargeLine;
  nonStackablePallet: SurchargeLine;
  oversizeOverweightSurcharge: SurchargeLine;
  remoteAreaCharges: SurchargeLine;
  restrictedDestinationSurcharge: SurchargeLine;
  totalOtherCharges: number;
  adjustmentAmount: number;
  fscPercent: number;
  fscPercentValue: number;
  fscPercentEditable: boolean;
  fsc: number;
  fscEditable: boolean;
  discountPercent: number;
  discountAmount: number;
  totalDiscount: number;
  freightAfterDiscount: number;
  subtotal: number;
  nonTaxableAmount: number;
  taxableAmount: number;
  gstPercent: number;
  cgst: number;
  cgstEditable: boolean;
  sgst: number;
  sgstEditable: boolean;
  grandTotal: number;
  grandTotalEditable: boolean;
  totalCharges: number;
  gstAmount: number;
  finalAmount: number;
  totalReceived: number;
  balance: number;
  paymentReceipts: PaymentReceiptLine[];
  paidAmount: number;
  balanceAmount: number;
  salesInvoiceDate: string;
  salesInvoiceNumber: string;
  salesInvoiceRemarks: string;
  pastInvoiceNo: string;
  creditDebitNote: string;
  invoiceRemarks1: string;
  invoiceRemarks2: string;
  invoiceRemarks3: string;
  invoiceRemarks4: string;
  refundAmount: number;
  refundDate: string;
  refundMode: string;
  refundRemarks: string;
}

interface PurchaseBilling {
  company: string;
  currency: string;
  gstType: string;
  gstApplicable: boolean;
  gstApplicableEditable: boolean;
  freight: number;
  freightEditable: boolean;
  freightPerKg: number;
  searchCharge: string;
  addressCorrection: SurchargeLine;
  clearanceCharge: SurchargeLine;
  elevatedRiskSurcharge: SurchargeLine;
  ess: SurchargeLine;
  imitation: SurchargeLine;
  medicineHandlingCharges: SurchargeLine;
  nonStackablePallet: SurchargeLine;
  oversizeOverweightSurcharge: SurchargeLine;
  remoteAreaCharges: SurchargeLine;
  restrictedDestinationSurcharge: SurchargeLine;
  totalOtherCharges: number;
  fscPercent: number;
  fscPercentValue: number;
  fscPercentEditable: boolean;
  fsc: number;
  fscEditable: boolean;
  discountPercent: number;
  discountAmount: number;
  totalDiscount: number;
  freightAfterDiscount: number;
  subtotal: number;
  nonTaxableAmount: number;
  taxableAmount: number;
  gstPercent: number;
  cgst: number;
  cgstEditable: boolean;
  sgst: number;
  sgstEditable: boolean;
  grandTotal: number;
  grandTotalEditable: boolean;
  totalCharges: number;
  gstAmount: number;
  finalAmount: number;
  cftContract: string;
  cftId: string;
  cftValue: number;
  vendorContract: string;
  vendorContractId: string;
  vendorTat: string;
  pastVendorInvoice: string;
  vendorInvoice1: string;
  invoiceRemarks1: string;
  vendorInvoice2: string;
  invoiceRemarks2: string;
  vendorInvoice3: string;
  invoiceRemarks3: string;
  vendorInvoice4: string;
  invoiceRemarks4: string;
}

interface AttachmentFile {
  id: string;
  file: File;
  category: string;
  preview?: string;
}

interface AWBFormData {
  awbNumber: string;
  customer: string;
  company: string;
  origin: string;
  originZone: string;
  destination: string;
  destinationZone: string;
  product: string;
  bookingDate: string;
  bookingTime: string;
  service: string;
  vendor: string;
  forwardingNumber: string;
  forwardingNumber2: string;
  referenceNumber: string;
  referenceName: string;
  shipmentValue: number;
  currency: string;
  invoiceDate: string;
  invoiceNumber: string;
  invoiceEditable: boolean;
  content: string;
  contractId: string;
  rateContract: string;
  cftContract: string;
  tat: string;
  remarks: string;
  paymentMethod: string;
  paymentMethodEditable: boolean;
  originHub: string;
  originHubEditable: boolean;
  licenseLocation: string;
  licenseLocationEditable: boolean;
  createShipmentInvoice: boolean;
  invoiceNote: string;
  invoiceNoteDescription: string;
  shipper: ShipperConsigneeData;
  consignee: ShipperConsigneeData;
  totalPieces: number;
  totalActualWeight: number;
  totalVolumetricWeight: number;
  consignorWeight: number;
  addWeight: number;
  totalChargeableWeight: number;
  weightDimensions: WeightDimension[];
  salesBilling: SalesBilling;
  purchaseBilling: PurchaseBilling;
  shipmentInvoice: ShipmentInvoice;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const NOTE_OPTIONS = [
  { value: 'gift from person to person', label: 'Gift from person to person' },
  { value: 'GIFT', label: 'Gift' },
  { value: 'SAMPLE', label: 'Sample' },
];

// ============================================================================
// API HELPER
// ============================================================================

const createAxios = () => {
  const API_BASE_URL =
    import.meta.env.VITE_API_URL || 'https://awbbackend.bliitz.in';
  const instance = axios.create({ baseURL: API_BASE_URL });

  instance.interceptors.request.use(config => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  return instance;
};

// ============================================================================
// DEFAULT VALUES
// ============================================================================

const getDefaultSurcharge = (): SurchargeLine => ({
  enabled: false,
  amount: 0,
  quantity: 1,
});

const getDefaultShipperConsignee = (): ShipperConsigneeData => ({
  code: '',
  company: '',
  personName: '',
  address1: '',
  address2: '',
  address3: '',
  postZipCode: '',
  city: '',
  stateCounty: '',
  country: '',
  countryCode: '',
  phoneNumber: '',
  phoneNumber2: '',
  emailAddress: '',
  kycType: '',
  kycNumber: '',
  kycDocuments: [],
  kycUpload: null,
  saveToAddressBook: false,
  updateAddressBook: false,
});

const getDefaultFormValues = (): AWBFormData => ({
  awbNumber: '',
  customer: '',
  company: '',
  origin: '',
  originZone: '',
  destination: '',
  destinationZone: '',
  product: '',
  bookingDate: new Date().toISOString().split('T')[0],
  bookingTime: new Date().toTimeString().slice(0, 5),
  service: '',
  vendor: '',
  forwardingNumber: '',
  forwardingNumber2: '',
  referenceNumber: '',
  referenceName: '',
  shipmentValue: 0,
  currency: '',
  invoiceDate: '',
  invoiceNumber: '',
  invoiceEditable: false,
  content: '',
  contractId: '',
  rateContract: '',
  cftContract: '',
  tat: '',
  remarks: '',
  paymentMethod: 'PREPAID',
  paymentMethodEditable: false,
  originHub: '',
  originHubEditable: false,
  licenseLocation: '',
  licenseLocationEditable: false,
  createShipmentInvoice: false,
  invoiceNote: '',
  invoiceNoteDescription: '',
  shipper: getDefaultShipperConsignee(),
  consignee: getDefaultShipperConsignee(),
  totalPieces: 1,
  totalActualWeight: 0,
  totalVolumetricWeight: 0,
  consignorWeight: 0,
  addWeight: 0,
  totalChargeableWeight: 0,
  weightDimensions: [
    {
      parcelNo: '',
      boxNo: 1,
      actualWeight: 0,
      length: 0,
      breadth: 0,
      height: 0,
      volumetricWeight: 0,
      chargeableWeight: 0,
    },
  ],
  salesBilling: {
    salesCurrency: '',
    gstType: 'IGST',
    gstApplicable: true,
    gstApplicableEditable: false,
    freight: 0,
    freightEditable: false,
    freightPerKg: 0,
    searchCharge: '',
    addressCorrection: getDefaultSurcharge(),
    clearanceCharge: getDefaultSurcharge(),
    elevatedRiskSurcharge: getDefaultSurcharge(),
    ess: getDefaultSurcharge(),
    imitation: getDefaultSurcharge(),
    medicineHandlingCharges: getDefaultSurcharge(),
    nonStackablePallet: getDefaultSurcharge(),
    oversizeOverweightSurcharge: getDefaultSurcharge(),
    remoteAreaCharges: getDefaultSurcharge(),
    restrictedDestinationSurcharge: getDefaultSurcharge(),
    totalOtherCharges: 0,
    adjustmentAmount: 0,
    fscPercent: 0,
    fscPercentValue: 0,
    fscPercentEditable: false,
    fsc: 0,
    fscEditable: false,
    discountPercent: 0,
    discountAmount: 0,
    totalDiscount: 0,
    freightAfterDiscount: 0,
    subtotal: 0,
    nonTaxableAmount: 0,
    taxableAmount: 0,
    gstPercent: 18,
    cgst: 0,
    cgstEditable: false,
    sgst: 0,
    sgstEditable: false,
    grandTotal: 0,
    grandTotalEditable: false,
    totalCharges: 0,
    gstAmount: 0,
    finalAmount: 0,
    totalReceived: 0,
    balance: 0,
    paymentReceipts: [],
    paidAmount: 0,
    balanceAmount: 0,
    salesInvoiceDate: '',
    salesInvoiceNumber: '',
    salesInvoiceRemarks: '',
    pastInvoiceNo: '',
    creditDebitNote: '',
    invoiceRemarks1: '',
    invoiceRemarks2: '',
    invoiceRemarks3: '',
    invoiceRemarks4: '',
    refundAmount: 0,
    refundDate: '',
    refundMode: '',
    refundRemarks: '',
  },
  purchaseBilling: {
    company: '',
    currency: '',
    gstType: 'IGST',
    gstApplicable: true,
    gstApplicableEditable: false,
    freight: 0,
    freightEditable: false,
    freightPerKg: 0,
    searchCharge: '',
    addressCorrection: getDefaultSurcharge(),
    clearanceCharge: getDefaultSurcharge(),
    elevatedRiskSurcharge: getDefaultSurcharge(),
    ess: getDefaultSurcharge(),
    imitation: getDefaultSurcharge(),
    medicineHandlingCharges: getDefaultSurcharge(),
    nonStackablePallet: getDefaultSurcharge(),
    oversizeOverweightSurcharge: getDefaultSurcharge(),
    remoteAreaCharges: getDefaultSurcharge(),
    restrictedDestinationSurcharge: getDefaultSurcharge(),
    totalOtherCharges: 0,
    fscPercent: 0,
    fscPercentValue: 0,
    fscPercentEditable: false,
    fsc: 0,
    fscEditable: false,
    discountPercent: 0,
    discountAmount: 0,
    totalDiscount: 0,
    freightAfterDiscount: 0,
    subtotal: 0,
    nonTaxableAmount: 0,
    taxableAmount: 0,
    gstPercent: 18,
    cgst: 0,
    cgstEditable: false,
    sgst: 0,
    sgstEditable: false,
    grandTotal: 0,
    grandTotalEditable: false,
    totalCharges: 0,
    gstAmount: 0,
    finalAmount: 0,
    cftContract: '',
    cftId: '',
    cftValue: 0,
    vendorContract: '',
    vendorContractId: '',
    vendorTat: '',
    pastVendorInvoice: '',
    vendorInvoice1: '',
    invoiceRemarks1: '',
    vendorInvoice2: '',
    invoiceRemarks2: '',
    vendorInvoice3: '',
    invoiceRemarks3: '',
    vendorInvoice4: '',
    invoiceRemarks4: '',
  },
  shipmentInvoice: {
    invoiceType: 'PROFORMA_INVOICE',
    currency: '',
    incoterms: 'FOB',
    note: 'gift from person to person',
    remarks: '',
    lineItems: [
      {
        boxNo: '1',
        srNo: 1,
        description: '',
        hsCode: '',
        unitType: 'PCS',
        quantity: 0,
        unitWeight: 0,
        igst: 0,
        unitRate: 0,
        amount: 0,
      },
    ],
  },
});



// ============================================================================
// EXTERNAL HELPER COMPONENTS (defined outside to prevent re-creation on re-render)
// ============================================================================

const FormField: React.FC<{
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
  className?: string;
}> = React.memo(({ label, required, error, children, className = '' }) => (
  <div className={`space-y-1 ${className}`}>
    <Label
      className={`text-xs font-medium ${
        required ? 'text-red-600' : 'text-gray-700 dark:text-gray-300'
      }`}
    >
      {label}
    </Label>
    {children}
    {error && (
      <p className='text-xs text-red-500 flex items-center gap-1'>
        <AlertCircle className='h-3 w-3' />
        {error}
      </p>
    )}
  </div>
));

FormField.displayName = 'FormField';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const AWBCreatePageNew: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const api = createAxios();

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<
    'awb' | 'sales' | 'purchase' | 'extra'
  >('awb');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const validationErrorRef = useRef<HTMLDivElement | null>(null);
  const [isSavingSalesBilling, setIsSavingSalesBilling] = useState(false);
  const [isSavingPurchaseBilling, setIsSavingPurchaseBilling] = useState(false);
  const [awbNumber, setAwbNumber] = useState<string>('');

  // Master data state
  const [masterData, setMasterData] = useState<MasterData>({
    customers: [],
    companies: [],
    countries: [],
    products: [],
    services: [],
    currencies: [],
    vendors: [],
    zones: [],
    incoterms: [],
    hubs: [],
    licenseLocations: [],
    freeFormNotes: [],
    isLoading: true,
  });

  // Form setup
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    trigger,
    getValues,
    formState: { errors, isValid, isSubmitting: formIsSubmitting },
  } = useForm<AWBFormData>({
    defaultValues: getDefaultFormValues(),
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  // Weight dimensions field array
  const {
    fields: weightFields,
    append: appendWeight,
    remove: removeWeight,
  } = useFieldArray({
    control,
    name: 'weightDimensions',
  });

  // Invoice line items field array
  const {
    fields: invoiceFields,
    append: appendInvoiceItem,
    remove: removeInvoiceItem,
  } = useFieldArray({
    control,
    name: 'shipmentInvoice.lineItems',
  });

  // Watch form values
  const watchedValues = watch();
  const totalPieces = watch('totalPieces');
  const weightDimensions = watch('weightDimensions');
  const createShipmentInvoice = watch('createShipmentInvoice');

  const scrollToValidationErrors = () => {
    if (!validationErrorRef.current) return;
    validationErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleViewErrors = () => {
    if (activeTab !== 'awb') {
      setActiveTab('awb');
      return;
    }
    scrollToValidationErrors();
  };

  // ============================================================================
  // DATA LOADING
  // ============================================================================

  useEffect(() => {
    // Load master data for create mode
    loadMasterData();
  }, []);

  useEffect(() => {
    if (validationErrors.length === 0) return;
    if (activeTab !== 'awb') return;
    const timer = window.setTimeout(() => {
      validationErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
    return () => window.clearTimeout(timer);
  }, [activeTab, validationErrors]);

  const fetchCountries = async (inputValue: string) => {
    try {
      const response = await api.get(`/api/master-data/country`, {
        params: { search: inputValue, limit: 50 },
      });
      const items = response.data.data?.data || response.data.data || [];
      return Array.isArray(items)
        ? items.map((c: any) => ({ value: c._id, label: c.name }))
        : [];
    } catch (error) {
      console.error('Error fetching countries', error);
      return [];
    }
  };

  const fetchCurrencies = async (inputValue: string) => {
    try {
      const response = await api.get(`/api/master-data/currency`, {
        params: { search: inputValue, limit: 50 },
      });
      const items = response.data.data?.data || response.data.data || [];
      return Array.isArray(items)
        ? items.map((c: any) => ({
            value: c._id,
            label: `${c.symbol ? `${c.symbol} ` : ''}${c.name} (${c.code})`,
          }))
        : [];
    } catch (error) {
      console.error('Error fetching currencies', error);
      return [];
    }
  };

  const loadMasterData = async (): Promise<void> => {
    setMasterData(prev => ({ ...prev, isLoading: true }));
    try {
      const [
        customers,
        companies,
        countries,
        products,
        services,
        currencies,
        vendors,
        zones,
        incoterms,
        hubs,
        licenseLocations,
        freeFormNotes,
      ] = await Promise.all([
        api.get('/api/master-data/customer'),
        api.get('/api/master-data/company'),
        api.get('/api/master-data/country'),
        api.get('/api/master-data/product'),
        api.get('/api/master-data/service'),
        api.get('/api/master-data/currency'),
        api.get('/api/master-data/vendor'),
        api.get('/api/master-data/zone'),
        api
          .get('/api/master-data/incoterm')
          .catch(() => ({ data: { data: { data: [] } } })),
        api
          .get('/api/master-data/hub')
          .catch(() => ({ data: { data: { data: [] } } })),
        api
          .get('/api/master-data/license-location')
          .catch(() => ({ data: { data: { data: [] } } })),
        api
          .get('/api/master-data/free-form-note')
          .catch(() => ({ data: { data: { data: [] } } })),
      ]);

      setMasterData({
        customers: customers.data.data?.data || [],
        companies: companies.data.data?.data || [],
        countries: countries.data.data?.data || [],
        products: products.data.data?.data || [],
        services: services.data.data?.data || [],
        currencies: currencies.data.data?.data || [],
        vendors: vendors.data.data?.data || [],
        zones: zones.data.data?.data || [],
        incoterms: incoterms.data.data?.data || [],
        hubs: hubs.data.data?.data || [],
        licenseLocations: licenseLocations.data.data?.data || [],
        freeFormNotes: freeFormNotes.data.data?.data || [],
        isLoading: false,
      });
    } catch (error) {
      console.error('Error loading master data:', error);
      setMasterData(prev => ({ ...prev, isLoading: false }));
    }
  };

  // Set loading to false after master data loads
  useEffect(() => {
    if (!masterData.isLoading) {
      setIsLoading(false);
    }
  }, [masterData.isLoading]);

  // Auto-populate consignee country from destination
  useEffect(() => {
    const destination = watch('destination');
    if (destination) {
      // Set the consignee country to match the destination
      setValue('consignee.country', destination);
    }
  }, [watchedValues.destination]);

  // Set default values for Shipper Country and Origin Hub
  useEffect(() => {
    const setDefaults = async () => {
      const isMumbaiHub = (hub: any) => {
        const name = hub?.name?.toLowerCase() || '';
        const code = hub?.code?.toLowerCase() || '';
        return name.includes('mumbai') || code.startsWith('mum');
      };

      // 1. Set Shipper City to Mumbai if empty
      if (!getValues('shipper.city')) {
        setValue('shipper.city', 'Mumbai');
      }

      // 2. Set Shipper Country to India
      if (!getValues('shipper.country')) {
        // Try to find in loaded master data first
        let india = masterData.countries.find(
          c => c.name.toLowerCase() === 'india' || c.code === 'IN'
        );

        // If not found, try to fetch it specifically
        if (!india) {
          try {
            const res = await api.get('/api/master-data/country', {
              params: { search: 'India' },
            });
            const found = res.data.data?.data?.find(
              (c: any) => c.name.toLowerCase() === 'india'
            );
            if (found) {
              india = found;
              // Optionally update masterData to include it so the dropdown label works
              setMasterData(prev => ({
                ...prev,
                countries: [...prev.countries, found],
              }));
            }
          } catch (e) {
            console.error('Failed to fetch default country India', e);
          }
        }

        if (india) {
          setValue('shipper.country', india._id);
        }
      }

      // 3. Set Origin Hub to Mumbai
      if (!getValues('originHub')) {
        let mumbai = masterData.hubs.find(isMumbaiHub);

        if (!mumbai) {
          try {
            const res = await api.get('/api/master-data/hub', {
              params: { search: 'Mumbai' },
            });
            const found = res.data.data?.data?.find((h: any) => isMumbaiHub(h));
            if (found) {
              mumbai = found;
              // Update masterData to include it
              setMasterData(prev => ({
                ...prev,
                hubs: [...prev.hubs, found],
              }));
            }
          } catch (e) {
            console.error('Failed to fetch default hub Mumbai', e);
          }
        }

        if (mumbai) {
          setValue('originHub', mumbai._id);
        }
      }
    };

    if (!masterData.isLoading) {
      setDefaults();
    }
  }, [masterData.isLoading, setValue, getValues]);

  // Set default currency to INR for shipment invoice when master data loads
  useEffect(() => {
    if (!masterData.isLoading && masterData.currencies.length > 0) {
      const currentCurrency = watch('shipmentInvoice.currency');
      if (!currentCurrency) {
        // Find INR currency
        const inrCurrency = masterData.currencies.find(
          c =>
            c.code === 'INR' ||
            c.name === 'INR' ||
            c.code?.toUpperCase() === 'INR'
        );
        if (inrCurrency) {
          setValue('shipmentInvoice.currency', inrCurrency._id);
        }
      }
    }

    if (!masterData.isLoading && masterData.countries.length > 0) {
      const currentShipperCountry = watch('shipper.country');
      if (!currentShipperCountry) {
        const indiaCountry = masterData.countries.find(
          c =>
            c.name === 'India' ||
            c.code === 'IN' ||
            c.name.toLowerCase() === 'india'
        );
        if (indiaCountry) {
          setValue('shipper.country', indiaCountry._id);
        }
      }
    }
  }, [masterData.isLoading, masterData.currencies, masterData.countries]);

  // ============================================================================
  // WEIGHT CALCULATIONS
  // ============================================================================

  // Watch all weight dimension fields for changes
  const weightDimensionFields = watch('weightDimensions');

  useEffect(() => {
    if (weightDimensionFields && weightDimensionFields.length > 0) {
      let totalActual = 0;
      let totalVolumetric = 0;

      weightDimensionFields.forEach((wd, index) => {
        if (wd) {
          const vol = (wd.length * wd.breadth * wd.height) / 5000;
          const chargeable = Math.max(wd.actualWeight, vol);

          totalActual += wd.actualWeight || 0;
          totalVolumetric += vol;

          setValue(
            `weightDimensions.${index}.volumetricWeight`,
            parseFloat(vol.toFixed(2))
          );
          setValue(
            `weightDimensions.${index}.chargeableWeight`,
            parseFloat(chargeable.toFixed(2))
          );
        }
      });

      setValue('totalActualWeight', parseFloat(totalActual.toFixed(2)));
      setValue('totalVolumetricWeight', parseFloat(totalVolumetric.toFixed(2)));

      const consignorWt = watchedValues.consignorWeight || 0;
      const addWt = watchedValues.addWeight || 0;
      const totalChargeable =
        Math.max(totalActual, totalVolumetric) + consignorWt + addWt;
      setValue('totalChargeableWeight', parseFloat(totalChargeable.toFixed(2)));
    }
  }, [
    JSON.stringify(weightDimensionFields), // Watch all weight dimension changes
    watchedValues.consignorWeight,
    watchedValues.addWeight,
    setValue,
  ]);

  // Update total pieces when weight dimensions change (for manual add/remove)
  useEffect(() => {
    const currentWeightCount = weightFields.length;
    const currentTotalPieces = watch('totalPieces') || 1;

    if (currentWeightCount !== currentTotalPieces) {
      setValue('totalPieces', currentWeightCount);
    }
  }, [weightFields.length, watch, setValue]);

  // ============================================================================
  // SALES BILLING CALCULATIONS
  // ============================================================================

  // Calculate FSC based on percentage
  useEffect(() => {
    const freight = watchedValues.salesBilling?.freight || 0;
    const fscPercent = watchedValues.salesBilling?.fscPercent || 0;
    const fscPercentValue = watchedValues.salesBilling?.fscPercentValue || 0;

    if (freight > 0 && fscPercent > 0 && fscPercentValue === 0) {
      const calculatedFsc = (freight * fscPercent) / 100;
      setValue(
        'salesBilling.fscPercentValue',
        parseFloat(calculatedFsc.toFixed(2))
      );
      if (!watchedValues.salesBilling?.fscEditable) {
        setValue('salesBilling.fsc', parseFloat(calculatedFsc.toFixed(2)));
      }
    }
  }, [
    watchedValues.salesBilling?.freight,
    watchedValues.salesBilling?.fscPercent,
  ]);

  // Calculate discount amount based on percentage
  useEffect(() => {
    const freight = watchedValues.salesBilling?.freight || 0;
    const discountPercent = watchedValues.salesBilling?.discountPercent || 0;

    if (freight > 0 && discountPercent > 0) {
      const calculatedDiscount = (freight * discountPercent) / 100;
      setValue(
        'salesBilling.discountAmount',
        parseFloat(calculatedDiscount.toFixed(2))
      );
    }
  }, [
    watchedValues.salesBilling?.freight,
    watchedValues.salesBilling?.discountPercent,
  ]);

  // Calculate totals
  useEffect(() => {
    const freight = watchedValues.salesBilling?.freight || 0;
    const fsc = watchedValues.salesBilling?.fsc || 0;
    const totalOtherCharges =
      watchedValues.salesBilling?.totalOtherCharges || 0;
    const adjustmentAmount = watchedValues.salesBilling?.adjustmentAmount || 0;
    const discountAmount = watchedValues.salesBilling?.discountAmount || 0;
    const gstApplicable = watchedValues.salesBilling?.gstApplicable;

    // Freight after discount (ensure it never goes below 0)
    const freightAfterDiscount = Math.max(0, freight - discountAmount);
    setValue(
      'salesBilling.freightAfterDiscount',
      parseFloat(freightAfterDiscount.toFixed(2))
    );

    // Subtotal (freight + fsc + other charges + adjustment - discount)
    const subtotal =
      freightAfterDiscount + fsc + totalOtherCharges + adjustmentAmount;
    setValue('salesBilling.subtotal', parseFloat(subtotal.toFixed(2)));

    // GST calculations
    const gstPercent = watchedValues.salesBilling?.gstPercent || 18;
    if (gstApplicable) {
      const taxableAmount = subtotal;
      setValue(
        'salesBilling.taxableAmount',
        parseFloat(taxableAmount.toFixed(2))
      );

      const gstAmount = (taxableAmount * gstPercent) / 100;
      setValue('salesBilling.gstAmount', parseFloat(gstAmount.toFixed(2)));

      const gstType = watchedValues.salesBilling?.gstType || 'IGST';
      if (gstType === 'CGST_SGST') {
        const halfGst = gstAmount / 2;
        setValue('salesBilling.cgst', parseFloat(halfGst.toFixed(2)));
        setValue('salesBilling.sgst', parseFloat(halfGst.toFixed(2)));
      } else {
        setValue('salesBilling.cgst', 0);
        setValue('salesBilling.sgst', gstAmount);
      }

      const grandTotal = taxableAmount + gstAmount;
      setValue('salesBilling.grandTotal', parseFloat(grandTotal.toFixed(2)));
      setValue('salesBilling.finalAmount', parseFloat(grandTotal.toFixed(2)));
    } else {
      setValue('salesBilling.taxableAmount', 0);
      setValue('salesBilling.gstAmount', 0);
      setValue('salesBilling.cgst', 0);
      setValue('salesBilling.sgst', 0);
      setValue('salesBilling.grandTotal', parseFloat(subtotal.toFixed(2)));
      setValue('salesBilling.finalAmount', parseFloat(subtotal.toFixed(2)));
    }

    // Calculate balance
    const paidAmount = watchedValues.salesBilling?.paidAmount || 0;
    const grandTotal = watchedValues.salesBilling?.grandTotal || subtotal;
    const balance = grandTotal - paidAmount;
    setValue('salesBilling.balance', parseFloat(balance.toFixed(2)));
    setValue('salesBilling.balanceAmount', parseFloat(balance.toFixed(2)));
  }, [
    watchedValues.salesBilling?.freight,
    watchedValues.salesBilling?.fsc,
    watchedValues.salesBilling?.totalOtherCharges,
    watchedValues.salesBilling?.adjustmentAmount,
    watchedValues.salesBilling?.discountAmount,
    watchedValues.salesBilling?.gstApplicable,
    watchedValues.salesBilling?.gstPercent,
    watchedValues.salesBilling?.gstType,
    watchedValues.salesBilling?.paidAmount,
  ]);

  // ============================================================================
  // PURCHASE BILLING CALCULATIONS
  // ============================================================================

  // Calculate FSC based on percentage for purchase billing
  useEffect(() => {
    const freight = watchedValues.purchaseBilling?.freight || 0;
    const fscPercent = watchedValues.purchaseBilling?.fscPercent || 0;
    const fscPercentValue = watchedValues.purchaseBilling?.fscPercentValue || 0;

    if (freight > 0 && fscPercent > 0 && fscPercentValue === 0) {
      const calculatedFsc = (freight * fscPercent) / 100;
      setValue(
        'purchaseBilling.fscPercentValue',
        parseFloat(calculatedFsc.toFixed(2))
      );
      if (!watchedValues.purchaseBilling?.fscEditable) {
        setValue('purchaseBilling.fsc', parseFloat(calculatedFsc.toFixed(2)));
      }
    }
  }, [
    watchedValues.purchaseBilling?.freight,
    watchedValues.purchaseBilling?.fscPercent,
  ]);

  // Calculate discount amount based on percentage for purchase billing
  useEffect(() => {
    const freight = watchedValues.purchaseBilling?.freight || 0;
    const discountPercent = watchedValues.purchaseBilling?.discountPercent || 0;

    if (freight > 0 && discountPercent > 0) {
      const calculatedDiscount = (freight * discountPercent) / 100;
      setValue(
        'purchaseBilling.discountAmount',
        parseFloat(calculatedDiscount.toFixed(2))
      );
    }
  }, [
    watchedValues.purchaseBilling?.freight,
    watchedValues.purchaseBilling?.discountPercent,
  ]);

  // Calculate purchase billing totals
  useEffect(() => {
    const freight = watchedValues.purchaseBilling?.freight || 0;
    const fsc = watchedValues.purchaseBilling?.fsc || 0;
    const totalOtherCharges =
      watchedValues.purchaseBilling?.totalOtherCharges || 0;
    const discountAmount = watchedValues.purchaseBilling?.discountAmount || 0;
    const gstApplicable = watchedValues.purchaseBilling?.gstApplicable;

    // Freight after discount
    const freightAfterDiscount = Math.max(0, freight - discountAmount);
    setValue(
      'purchaseBilling.freightAfterDiscount',
      parseFloat(freightAfterDiscount.toFixed(2))
    );

    // Subtotal (freight + fsc + other charges - discount)
    const subtotal = freightAfterDiscount + fsc + totalOtherCharges;
    setValue('purchaseBilling.subtotal', parseFloat(subtotal.toFixed(2)));

    // GST calculations
    const gstPercent = watchedValues.purchaseBilling?.gstPercent || 18;
    if (gstApplicable) {
      const taxableAmount = subtotal;
      setValue(
        'purchaseBilling.taxableAmount',
        parseFloat(taxableAmount.toFixed(2))
      );

      const gstAmount = (taxableAmount * gstPercent) / 100;
      setValue('purchaseBilling.gstAmount', parseFloat(gstAmount.toFixed(2)));

      const gstType = watchedValues.purchaseBilling?.gstType || 'IGST';
      if (gstType === 'CGST_SGST') {
        const halfGst = gstAmount / 2;
        setValue('purchaseBilling.cgst', parseFloat(halfGst.toFixed(2)));
        setValue('purchaseBilling.sgst', parseFloat(halfGst.toFixed(2)));
      } else {
        setValue('purchaseBilling.cgst', 0);
        setValue('purchaseBilling.sgst', gstAmount);
      }

      const grandTotal = taxableAmount + gstAmount;
      setValue('purchaseBilling.grandTotal', parseFloat(grandTotal.toFixed(2)));
      setValue(
        'purchaseBilling.finalAmount',
        parseFloat(grandTotal.toFixed(2))
      );
    } else {
      setValue('purchaseBilling.taxableAmount', 0);
      setValue('purchaseBilling.gstAmount', 0);
      setValue('purchaseBilling.cgst', 0);
      setValue('purchaseBilling.sgst', 0);
      setValue('purchaseBilling.grandTotal', parseFloat(subtotal.toFixed(2)));
      setValue('purchaseBilling.finalAmount', parseFloat(subtotal.toFixed(2)));
    }
  }, [
    watchedValues.purchaseBilling?.freight,
    watchedValues.purchaseBilling?.fsc,
    watchedValues.purchaseBilling?.totalOtherCharges,
    watchedValues.purchaseBilling?.discountAmount,
    watchedValues.purchaseBilling?.gstApplicable,
    watchedValues.purchaseBilling?.gstPercent,
    watchedValues.purchaseBilling?.gstType,
  ]);

  // ============================================================================
  // VALIDATION
  // ============================================================================

  const validateRequiredFields = (): string[] => {
    const validationErrors: string[] = [];
    const data = watch();

    if (!data.customer) validationErrors.push('Customer is required');
    if (!data.company) validationErrors.push('Company is required');
    if (!data.destination) validationErrors.push('Destination is required');
    if (!data.product) validationErrors.push('Product is required');
    if (!data.service) validationErrors.push('Service is required');
    if (!data.currency) validationErrors.push('Currency is required');
    if (!data.bookingDate) validationErrors.push('Booking Date is required');
    if (!data.bookingTime) validationErrors.push('Booking Time is required');
    if (!data.paymentMethod)
      validationErrors.push('Payment Method is required');

    if (!data.shipper.personName)
      validationErrors.push('Shipper Name is required');
    if (!data.shipper.address1)
      validationErrors.push('Shipper Address is required');
    if (!data.shipper.city) validationErrors.push('Shipper City is required');
    if (!data.shipper.country)
      validationErrors.push('Shipper Country is required');
    if (!data.shipper.phoneNumber)
      validationErrors.push('Shipper Phone is required');

    if (!data.consignee.personName)
      validationErrors.push('Consignee Name is required');
    if (!data.consignee.address1)
      validationErrors.push('Consignee Address is required');
    if (!data.consignee.city)
      validationErrors.push('Consignee City is required');
    if (!data.consignee.country)
      validationErrors.push('Consignee Country is required');
    if (!data.consignee.phoneNumber)
      validationErrors.push('Consignee Phone is required');

    if (data.totalPieces < 1)
      validationErrors.push('Total Pieces must be at least 1');

    const hasWeight = data.weightDimensions.some(wd => wd.actualWeight > 0);
    if (!hasWeight)
      validationErrors.push('At least one package must have weight');

    return validationErrors;
  };

  // ============================================================================
  // FORM SUBMISSION
  // ============================================================================

  const onSubmit = async (data: AWBFormData) => {
    const customErrors = validateRequiredFields();
    if (customErrors.length > 0) {
      setValidationErrors(customErrors);
      if (activeTab !== 'awb') {
        setActiveTab('awb');
      }
      toast({
        title: 'Validation Error',
        description: `Please fix ${customErrors.length} error(s) before submitting`,
        variant: 'destructive',
      });
      return;
    }
    setValidationErrors([]);

    setIsSubmitting(true);
    try {
      const submitData = {
        customer: data.customer,
        company: data.company,
        origin: data.origin || undefined,
        destination: data.destination,
        product: data.product,
        bookingDate: new Date(data.bookingDate),
        bookingTime: data.bookingTime,
        service: data.service,
        vendor: data.vendor || undefined,
        originHub: data.originHub || undefined,
        forwardingNumber: data.forwardingNumber || undefined,
        forwardingNumber2: data.forwardingNumber2 || undefined,
        referenceNumber: data.referenceNumber || undefined,
        referenceName: data.referenceName || undefined,
        shipmentValue: data.shipmentValue || 0,
        currency: data.currency,
        invoiceDate: data.invoiceDate ? new Date(data.invoiceDate) : undefined,
        invoiceNumber: data.invoiceNumber || undefined,
        content: data.content || undefined,
        rateContract: data.rateContract || undefined,
        cftContract: data.cftContract || undefined,
        tat: data.tat || undefined,
        remarks: data.remarks || undefined,
        paymentMethod: data.paymentMethod as any,
        shipper: {
          code: data.shipper.code || '',
          personName: data.shipper.personName || '',
          company: data.shipper.company || undefined,
          address1: data.shipper.address1,
          address2: data.shipper.address2 || undefined,
          address3: data.shipper.address3 || undefined,
          postZipCode: data.shipper.postZipCode || undefined,
          city: data.shipper.city,
          stateCounty: data.shipper.stateCounty || '',
          country: data.shipper.country,
          phoneNumber: data.shipper.phoneNumber,
          emailAddress: data.shipper.emailAddress || undefined,
          kycType: (data.shipper.kycType as any) || undefined,
          kycNumber: data.shipper.kycNumber || undefined,
          kycDocuments: data.shipper.kycDocuments || [],
        },
        consignee: {
          code: data.consignee.code || '',
          personName: data.consignee.personName || '',
          company: data.consignee.company || undefined,
          address1: data.consignee.address1,
          address2: data.consignee.address2 || undefined,
          address3: data.consignee.address3 || undefined,
          postZipCode: data.consignee.postZipCode || undefined,
          city: data.consignee.city,
          stateCounty: data.consignee.stateCounty || '',
          country: data.consignee.country,
          phoneNumber: data.consignee.phoneNumber,
          emailAddress: data.consignee.emailAddress || undefined,
          kycType: (data.consignee.kycType as any) || undefined,
          kycNumber: data.consignee.kycNumber || undefined,
          kycDocuments: data.consignee.kycDocuments || [],
        },
        totalPieces: data.totalPieces || 1,
        consignorWeight: data.consignorWeight || undefined,
        addWeight: data.addWeight || undefined,
        weightDimensions: data.weightDimensions.map(wd => ({
          boxNo: wd.boxNo,
          actualWeight: wd.actualWeight || 0,
          length: wd.length || 0,
          breadth: wd.breadth || 0,
          height: wd.height || 0,
        })),
        salesBilling:
          data.salesBilling.freight > 0 ||
          data.salesBilling.totalCharges > 0 ||
          data.salesBilling.grandTotal > 0
            ? {
                salesCurrency: data.salesBilling.salesCurrency || data.currency,
                currency: data.salesBilling.salesCurrency || data.currency,
                gstType: data.salesBilling.gstType || 'IGST',
                gstApplicable: data.salesBilling.gstApplicable !== false,
                freight: data.salesBilling.freight || 0,
                freightPerKg: data.salesBilling.freightPerKg || 0,
                fsc: data.salesBilling.fsc || 0,
                fscPercent: data.salesBilling.fscPercent || 0,

                // Individual Surcharges
                addressCorrection: {
                  enabled:
                    data.salesBilling.addressCorrection?.enabled || false,
                  amount: data.salesBilling.addressCorrection?.amount || 0,
                  quantity: data.salesBilling.addressCorrection?.quantity || 0,
                },
                clearanceCharge: {
                  enabled: data.salesBilling.clearanceCharge?.enabled || false,
                  amount: data.salesBilling.clearanceCharge?.amount || 0,
                  quantity: data.salesBilling.clearanceCharge?.quantity || 0,
                },
                elevatedRiskSurcharge: {
                  enabled:
                    data.salesBilling.elevatedRiskSurcharge?.enabled || false,
                  amount: data.salesBilling.elevatedRiskSurcharge?.amount || 0,
                  quantity:
                    data.salesBilling.elevatedRiskSurcharge?.quantity || 0,
                },
                ess: {
                  enabled: data.salesBilling.ess?.enabled || false,
                  amount: data.salesBilling.ess?.amount || 0,
                  quantity: data.salesBilling.ess?.quantity || 0,
                },
                imitation: {
                  enabled: data.salesBilling.imitation?.enabled || false,
                  amount: data.salesBilling.imitation?.amount || 0,
                  quantity: data.salesBilling.imitation?.quantity || 0,
                },
                medicineHandlingCharges: {
                  enabled:
                    data.salesBilling.medicineHandlingCharges?.enabled || false,
                  amount:
                    data.salesBilling.medicineHandlingCharges?.amount || 0,
                  quantity:
                    data.salesBilling.medicineHandlingCharges?.quantity || 0,
                },
                nonStackablePallet: {
                  enabled:
                    data.salesBilling.nonStackablePallet?.enabled || false,
                  amount: data.salesBilling.nonStackablePallet?.amount || 0,
                  quantity: data.salesBilling.nonStackablePallet?.quantity || 0,
                },
                oversizeOverweightSurcharge: {
                  enabled:
                    data.salesBilling.oversizeOverweightSurcharge?.enabled ||
                    false,
                  amount:
                    data.salesBilling.oversizeOverweightSurcharge?.amount || 0,
                  quantity:
                    data.salesBilling.oversizeOverweightSurcharge?.quantity ||
                    0,
                },
                remoteAreaCharges: {
                  enabled:
                    data.salesBilling.remoteAreaCharges?.enabled || false,
                  amount: data.salesBilling.remoteAreaCharges?.amount || 0,
                  quantity: data.salesBilling.remoteAreaCharges?.quantity || 0,
                },
                restrictedDestinationSurcharge: {
                  enabled:
                    data.salesBilling.restrictedDestinationSurcharge?.enabled ||
                    false,
                  amount:
                    data.salesBilling.restrictedDestinationSurcharge?.amount ||
                    0,
                  quantity:
                    data.salesBilling.restrictedDestinationSurcharge
                      ?.quantity || 0,
                },

                // Surcharges Total
                totalOtherCharges: data.salesBilling.totalOtherCharges || 0,
                adjustmentAmount: data.salesBilling.adjustmentAmount || 0,
                discountPercent: data.salesBilling.discountPercent || 0,
                discountAmount: data.salesBilling.discountAmount || 0,
                totalDiscount: data.salesBilling.totalDiscount || 0,
                freightAfterDiscount:
                  data.salesBilling.freightAfterDiscount || 0,
                subtotal: data.salesBilling.subtotal || 0,
                nonTaxableAmount: data.salesBilling.nonTaxableAmount || 0,
                taxableAmount: data.salesBilling.taxableAmount || 0,
                gstPercent: data.salesBilling.gstPercent || 18,
                cgst: data.salesBilling.cgst || 0,
                sgst: data.salesBilling.sgst || 0,
                gstAmount: data.salesBilling.gstAmount || 0,
                grandTotal: data.salesBilling.grandTotal || 0,
                totalCharges: data.salesBilling.totalCharges || 0,
                finalAmount:
                  data.salesBilling.finalAmount ||
                  data.salesBilling.grandTotal ||
                  0,
                paidAmount: data.salesBilling.paidAmount || 0,
                balanceAmount: data.salesBilling.balanceAmount || 0,
                salesInvoiceNumber:
                  data.salesBilling.salesInvoiceNumber || undefined,
                salesInvoiceRemarks:
                  data.salesBilling.salesInvoiceRemarks || undefined,
              }
            : undefined,
        purchaseBilling:
          data.purchaseBilling.freight > 0 ||
          data.purchaseBilling.grandTotal > 0
            ? {
                company:
                  data.purchaseBilling.company || data.company || undefined,
                currency: data.purchaseBilling.currency || data.currency,
                gstType: data.purchaseBilling.gstType || 'IGST',
                gstApplicable: data.purchaseBilling.gstApplicable !== false,
                freight: data.purchaseBilling.freight || 0,
                freightPerKg: data.purchaseBilling.freightPerKg || 0,
                fsc: data.purchaseBilling.fsc || 0,
                fscPercent: data.purchaseBilling.fscPercent || 0,

                // Individual Surcharges
                addressCorrection: {
                  enabled:
                    data.purchaseBilling.addressCorrection?.enabled || false,
                  amount: data.purchaseBilling.addressCorrection?.amount || 0,
                  quantity:
                    data.purchaseBilling.addressCorrection?.quantity || 0,
                },
                clearanceCharge: {
                  enabled:
                    data.purchaseBilling.clearanceCharge?.enabled || false,
                  amount: data.purchaseBilling.clearanceCharge?.amount || 0,
                  quantity: data.purchaseBilling.clearanceCharge?.quantity || 0,
                },
                elevatedRiskSurcharge: {
                  enabled:
                    data.purchaseBilling.elevatedRiskSurcharge?.enabled ||
                    false,
                  amount:
                    data.purchaseBilling.elevatedRiskSurcharge?.amount || 0,
                  quantity:
                    data.purchaseBilling.elevatedRiskSurcharge?.quantity || 0,
                },
                ess: {
                  enabled: data.purchaseBilling.ess?.enabled || false,
                  amount: data.purchaseBilling.ess?.amount || 0,
                  quantity: data.purchaseBilling.ess?.quantity || 0,
                },
                imitation: {
                  enabled: data.purchaseBilling.imitation?.enabled || false,
                  amount: data.purchaseBilling.imitation?.amount || 0,
                  quantity: data.purchaseBilling.imitation?.quantity || 0,
                },
                medicineHandlingCharges: {
                  enabled:
                    data.purchaseBilling.medicineHandlingCharges?.enabled ||
                    false,
                  amount:
                    data.purchaseBilling.medicineHandlingCharges?.amount || 0,
                  quantity:
                    data.purchaseBilling.medicineHandlingCharges?.quantity || 0,
                },
                nonStackablePallet: {
                  enabled:
                    data.purchaseBilling.nonStackablePallet?.enabled || false,
                  amount: data.purchaseBilling.nonStackablePallet?.amount || 0,
                  quantity:
                    data.purchaseBilling.nonStackablePallet?.quantity || 0,
                },
                oversizeOverweightSurcharge: {
                  enabled:
                    data.purchaseBilling.oversizeOverweightSurcharge?.enabled ||
                    false,
                  amount:
                    data.purchaseBilling.oversizeOverweightSurcharge?.amount ||
                    0,
                  quantity:
                    data.purchaseBilling.oversizeOverweightSurcharge
                      ?.quantity || 0,
                },
                remoteAreaCharges: {
                  enabled:
                    data.purchaseBilling.remoteAreaCharges?.enabled || false,
                  amount: data.purchaseBilling.remoteAreaCharges?.amount || 0,
                  quantity:
                    data.purchaseBilling.remoteAreaCharges?.quantity || 0,
                },
                restrictedDestinationSurcharge: {
                  enabled:
                    data.purchaseBilling.restrictedDestinationSurcharge
                      ?.enabled || false,
                  amount:
                    data.purchaseBilling.restrictedDestinationSurcharge
                      ?.amount || 0,
                  quantity:
                    data.purchaseBilling.restrictedDestinationSurcharge
                      ?.quantity || 0,
                },

                // Surcharges & Totals
                totalOtherCharges: data.purchaseBilling.totalOtherCharges || 0,
                discountPercent: data.purchaseBilling.discountPercent || 0,
                discountAmount: data.purchaseBilling.discountAmount || 0,
                totalDiscount: data.purchaseBilling.totalDiscount || 0,
                freightAfterDiscount:
                  data.purchaseBilling.freightAfterDiscount || 0,
                subtotal: data.purchaseBilling.subtotal || 0,
                nonTaxableAmount: data.purchaseBilling.nonTaxableAmount || 0,
                taxableAmount: data.purchaseBilling.taxableAmount || 0,
                gstPercent: data.purchaseBilling.gstPercent || 18,
                cgst: data.purchaseBilling.cgst || 0,
                sgst: data.purchaseBilling.sgst || 0,
                gstAmount: data.purchaseBilling.gstAmount || 0,
                grandTotal: data.purchaseBilling.grandTotal || 0,
                totalCharges: data.purchaseBilling.totalCharges || 0,
                finalAmount:
                  data.purchaseBilling.finalAmount ||
                  data.purchaseBilling.grandTotal ||
                  0,
                cftId: data.purchaseBilling.cftId || undefined,
                cftValue: data.purchaseBilling.cftValue || 0,
                vendorContractId:
                  data.purchaseBilling.vendorContractId || undefined,
                vendorTat: data.purchaseBilling.vendorTat || undefined,
                pastVendorInvoice:
                  data.purchaseBilling.pastVendorInvoice || undefined,
                vendorInvoice1:
                  data.purchaseBilling.vendorInvoice1 || undefined,
                invoiceRemarks1:
                  data.purchaseBilling.invoiceRemarks1 || undefined,
                vendorInvoice2:
                  data.purchaseBilling.vendorInvoice2 || undefined,
                invoiceRemarks2:
                  data.purchaseBilling.invoiceRemarks2 || undefined,
              }
            : undefined,
        shipmentInvoice: data.shipmentInvoice.lineItems.some(
          item => item.description && item.quantity > 0
        )
          ? {
              invoiceType: data.shipmentInvoice.invoiceType,
              currency: data.shipmentInvoice.currency || data.currency,
              incoterms: data.shipmentInvoice.incoterms,
              note: data.shipmentInvoice.note || undefined,
              remarks: data.shipmentInvoice.remarks || undefined,
              lineItems: data.shipmentInvoice.lineItems.filter(
                item => item.description && item.quantity > 0
              ),
            }
          : undefined,
      };

      const newAWB = await awbService.createAWB(submitData as any);

      toast({
        title: 'Success',
        description: `AWB ${newAWB.awbNumber} created successfully`,
      });

      // Set awbNumber state so billing can be saved later
      setAwbNumber(newAWB.awbNumber);

      // Navigate to view page
      navigate(`/awb/${newAWB.awbNumber}/view`);
    } catch (error: any) {
      console.error('AWB creation error:', error);
      toast({
        title: 'Error',
        description:
          error.response?.data?.error?.message || 'Failed to create AWB',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================================================
  // RENDER: LOADING
  // ============================================================================

  if (isLoading || masterData.isLoading) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <LoadingSpinner size='lg' />
      </div>
    );
  }

  // ============================================================================
  // RENDER: TABS
  // ============================================================================

  const renderTabs = () => (
    <div className='flex overflow-x-auto border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'>
      {[
        { id: 'awb', label: 'AWB Details', shortLabel: 'AWB' },
        { id: 'sales', label: 'Sales Billing', shortLabel: '₹ SALES' },
        { id: 'purchase', label: 'Purchase Billing', shortLabel: '₹ PURCHASE' },
        // { id: 'extra', label: 'Extra', shortLabel: 'EXTRA' }, // Commented out for now
      ].map(tab => (
        <button
          key={tab.id}
          type='button'
          onClick={() => setActiveTab(tab.id as any)}
          className={`px-4 sm:px-6 py-3 text-xs sm:text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
            activeTab === tab.id
              ? 'border-blue-600 text-blue-600 bg-blue-50 dark:bg-blue-900/20'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <span className='hidden sm:inline'>{tab.label}</span>
          <span className='sm:hidden'>{tab.shortLabel}</span>
        </button>
      ))}
    </div>
  );

  // ============================================================================
  // RENDER: AWB DETAILS SECTION (Redesigned to match AWBViewPageNew layout)
  // ============================================================================

  const renderAWBDetailsSection = () => {
    return (
      <>
        <div className='bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'>
          {/* Section Header */}
          <div className='px-4 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700'>
            <h2 className='text-sm font-semibold text-gray-800 dark:text-gray-200'>
              AWB DETAILS
            </h2>
          </div>

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div
              ref={validationErrorRef}
              id='awb-validation-errors'
              role='alert'
              aria-live='polite'
              className='mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg'
            >
              <div className='flex items-center gap-2 text-red-800 font-medium text-sm mb-2'>
                <AlertCircle className='h-4 w-4' />
                Please fix the following errors:
              </div>
              <ul className='text-xs text-red-700 list-disc list-inside space-y-1'>
                {validationErrors.map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Content Grid - 3 Column Layout matching AWBViewPageNew */}
          <div className='grid grid-cols-1 lg:grid-cols-12 gap-0'>
            {/* LEFT COLUMN: Basic Info */}
            <div className='lg:col-span-4 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700 p-4 space-y-4'>
              {/* Basic Fields - matching AWBViewPageNew structure */}
              <div className='grid grid-cols-2 gap-3'>
                <FormField label='CUSTOMER *' required>
                  <Controller
                    name='customer'
                    control={control}
                    rules={{ required: 'Customer is required' }}
                    render={({ field }) => (
                      <SearchableSelect
                        options={masterData.customers.map(c => ({
                          value: c._id,
                          label: `${c.name} (${c.code})`,
                          searchText: `${c.name} ${c.code}`,
                        }))}
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder='Search...'
                        className='h-8 text-xs'
                      />
                    )}
                  />
                </FormField>
                <FormField label='COMPANY *' required>
                  <Controller
                    name='company'
                    control={control}
                    rules={{ required: 'Company is required' }}
                    render={({ field }) => (
                      <SearchableSelect
                        options={masterData.companies.map(c => ({
                          value: c._id,
                          label: c.name,
                        }))}
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder='Select...'
                        className='h-8 text-xs'
                      />
                    )}
                  />
                </FormField>
              </div>

              <div className='grid grid-cols-2 gap-3'>
                <FormField label='BOOKING DATE *' required>
                  <Input
                    type='date'
                    {...register('bookingDate')}
                    className='h-8 text-xs'
                  />
                </FormField>
                <FormField label='BOOKING TIME *' required>
                  <Input
                    type='time'
                    {...register('bookingTime')}
                    className='h-8 text-xs'
                  />
                </FormField>
              </div>

              <div className='grid grid-cols-12 gap-3'>
                <div className='col-span-12'>
                  <FormField label='ORIGIN'>
                    <Controller
                      name='origin'
                      control={control}
                      render={({ field }) => (
                        <AsyncSearchableSelect
                          loadOptions={fetchCountries}
                          defaultOptions={masterData.countries
                            .slice(0, 20)
                            .map(c => ({ value: c._id, label: c.name }))}
                          value={field.value}
                          onValueChange={field.onChange}
                          placeholder='Search Country...'
                          className='h-8 text-xs'
                        />
                      )}
                    />
                  </FormField>
                </div>
              </div>

              <div className='grid grid-cols-12 gap-3'>
                <div className='col-span-12'>
                  <FormField label='DESTINATION *' required>
                    <Controller
                      name='destination'
                      control={control}
                      rules={{ required: 'Destination is required' }}
                      render={({ field }) => (
                        <AsyncSearchableSelect
                          loadOptions={fetchCountries}
                          defaultOptions={masterData.countries
                            .slice(0, 20)
                            .map(c => ({ value: c._id, label: c.name }))}
                          value={field.value}
                          onValueChange={field.onChange}
                          placeholder='Search Country...'
                          className='h-8 text-xs bg-blue-50 border-blue-200'
                        />
                      )}
                    />
                  </FormField>
                </div>
              </div>

              <div className='grid grid-cols-2 gap-3'>
                <FormField label='PRODUCT *' required>
                  <Controller
                    name='product'
                    control={control}
                    rules={{ required: 'Product is required' }}
                    render={({ field }) => (
                      <SearchableSelect
                        options={masterData.products.map(p => ({
                          value: p._id,
                          label: p.name,
                        }))}
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder='Select...'
                        className='h-8 text-xs'
                      />
                    )}
                  />
                </FormField>
                <FormField label='SERVICE *' required>
                  <Controller
                    name='service'
                    control={control}
                    rules={{ required: 'Service is required' }}
                    render={({ field }) => (
                      <SearchableSelect
                        options={masterData.services.map(s => ({
                          value: s._id,
                          label: s.name,
                        }))}
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder='Select...'
                        className='h-8 text-xs'
                      />
                    )}
                  />
                </FormField>
              </div>

              <div className='grid grid-cols-2 gap-3'>
                <FormField label='VENDOR'>
                  <Controller
                    name='vendor'
                    control={control}
                    render={({ field }) => (
                      <SearchableSelect
                        options={masterData.vendors.map(v => ({
                          value: v._id,
                          label: `${v.name} (${v.code})`,
                          searchText: `${v.name} ${v.code}`,
                        }))}
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder='Search...'
                        className='h-8 text-xs'
                      />
                    )}
                  />
                </FormField>
                <FormField label='ORIGIN HUB'>
                  <Controller
                    name='originHub'
                    control={control}
                    render={({ field }) => (
                      <SearchableSelect
                        options={masterData.hubs.map(h => ({
                          value: h._id,
                          label: h.name,
                        }))}
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder='Select...'
                        className='h-8 text-xs'
                      />
                    )}
                  />
                </FormField>
              </div>

              <div className='grid grid-cols-2 gap-3'>
                <FormField label='FORWARDING NO.'>
                  <Input
                    {...register('forwardingNumber')}
                    className='h-8 text-xs'
                    placeholder=''
                  />
                </FormField>
                <FormField label='FORWARDING NO. 2'>
                  <Input
                    {...register('forwardingNumber2')}
                    className='h-8 text-xs'
                    placeholder=''
                  />
                </FormField>
              </div>

              <div className='grid grid-cols-2 gap-3'>
                <FormField label='REFERENCE NO.'>
                  <Input
                    {...register('referenceNumber')}
                    className='h-8 text-xs'
                    placeholder=''
                  />
                </FormField>
                <FormField label='REFERENCE NAME'>
                  <Input
                    {...register('referenceName')}
                    className='h-8 text-xs'
                    placeholder=''
                  />
                </FormField>
              </div>

              <div className='grid grid-cols-2 gap-3'>
                <FormField label='SHIPMENT VALUE'>
                  <Input
                    type='number'
                    step='0.01'
                    {...register('shipmentValue', { valueAsNumber: true })}
                    className='h-8 text-xs bg-blue-50 border-blue-200'
                    placeholder='0.00'
                  />
                </FormField>
                <FormField label='CURRENCY *' required>
                  <Controller
                    name='currency'
                    control={control}
                    rules={{ required: 'Currency is required' }}
                    render={({ field }) => (
                      <AsyncSearchableSelect
                        loadOptions={fetchCurrencies}
                        defaultOptions={masterData.currencies.map(c => ({
                          value: c._id,
                          label: `${c.symbol ? `${c.symbol} ` : ''}${c.name} (${c.code})`,
                        }))}
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder='Select...'
                        className='h-8 text-xs'
                        initialLabel={
                          masterData.currencies.find(c => c._id === field.value)
                            ?.code
                        }
                      />
                    )}
                  />
                </FormField>
              </div>

              <div className='grid grid-cols-2 gap-3'>
                <FormField label='PAYMENT METHOD *' required>
                  <Controller
                    name='paymentMethod'
                    control={control}
                    rules={{ required: 'Payment method is required' }}
                    render={({ field }) => (
                      <SearchableSelect
                        options={[
                          { value: 'PREPAID', label: 'Prepaid' },
                          { value: 'COD', label: 'COD' },
                          { value: 'CREDIT', label: 'Credit' },
                          { value: 'CASH', label: 'Cash' },
                          { value: 'CHEQUE', label: 'Cheque' },
                          { value: 'ONLINE', label: 'Online' },
                        ]}
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder='Select...'
                        className='h-8 text-xs'
                      />
                    )}
                  />
                </FormField>
                <FormField label='INVOICE NO.'>
                  <Input
                    {...register('invoiceNumber')}
                    className='h-8 text-xs'
                    placeholder=''
                  />
                </FormField>
              </div>

              <FormField label='REMARKS'>
                <Textarea
                  {...register('remarks')}
                  rows={2}
                  className='text-xs resize-none'
                  placeholder='Additional remarks...'
                />
              </FormField>
            </div>

            {/* MIDDLE COLUMN: Shipper & Consignee */}
            {/* MIDDLE COLUMN: Shipper & Consignee & Weight Summary */}
            <div className='lg:col-span-8 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700 p-4'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                {/* Shipper */}
                <div className='space-y-3'>
                  <div className='flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-200 border-b pb-2'>
                    <User className='h-4 w-4 text-green-600' />
                    SHIPPER
                  </div>
                  <FormField label='NAME *' required>
                    <Input
                      {...register('shipper.personName')}
                      className='h-8 text-xs'
                      placeholder=''
                    />
                  </FormField>
                  <FormField label='COMPANY'>
                    <Input
                      {...register('shipper.company')}
                      className='h-8 text-xs'
                      placeholder=''
                    />
                  </FormField>
                  <FormField label='ADDRESS *' required>
                    <Input
                      {...register('shipper.address1')}
                      className='h-8 text-xs'
                      placeholder='Address Line 1'
                    />
                  </FormField>
                  <Input
                    {...register('shipper.address2')}
                    className='h-8 text-xs'
                    placeholder='Address Line 2'
                  />
                  <Input
                    {...register('shipper.address3')}
                    className='h-8 text-xs'
                    placeholder='Address Line 3'
                  />
                  <div className='grid grid-cols-2 gap-2'>
                    <FormField label='CITY *' required>
                      <Input
                        {...register('shipper.city')}
                        className='h-8 text-xs'
                        placeholder=''
                      />
                    </FormField>
                    <FormField label='STATE'>
                      <Input
                        {...register('shipper.stateCounty')}
                        className='h-8 text-xs'
                        placeholder=''
                      />
                    </FormField>
                  </div>
                  <div className='grid grid-cols-2 gap-2'>
                    <FormField label='COUNTRY *' required>
                      <Controller
                        name='shipper.country'
                        control={control}
                        rules={{ required: 'Country is required' }}
                        render={({ field }) => (
                          <AsyncSearchableSelect
                            loadOptions={fetchCountries}
                            defaultOptions={masterData.countries
                              .slice(0, 20)
                              .map(c => ({ value: c._id, label: c.name }))}
                            value={field.value}
                            onValueChange={field.onChange}
                            placeholder='Search Country...'
                            className='h-8 text-xs'
                            initialLabel={
                              masterData.countries.find(
                                c => c._id === field.value
                              )?.name
                            }
                          />
                        )}
                      />
                    </FormField>
                    <FormField label='ZIP'>
                      <Input
                        {...register('shipper.postZipCode')}
                        className='h-8 text-xs'
                        placeholder=''
                      />
                    </FormField>
                  </div>
                  <FormField label='PHONE *' required>
                    <Input
                      {...register('shipper.phoneNumber')}
                      className='h-8 text-xs'
                      placeholder=''
                    />
                  </FormField>
                  <FormField label='EMAIL'>
                    <Input
                      type='email'
                      {...register('shipper.emailAddress')}
                      className='h-8 text-xs'
                      placeholder=''
                    />
                  </FormField>
                </div>

                {/* Consignee */}
                <div className='space-y-3'>
                  <div className='flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-200 border-b pb-2'>
                    <MapPin className='h-4 w-4 text-red-600' />
                    CONSIGNEE
                  </div>
                  <FormField label='NAME *' required>
                    <Input
                      {...register('consignee.personName')}
                      className='h-8 text-xs'
                      placeholder=''
                    />
                  </FormField>
                  <FormField label='COMPANY'>
                    <Input
                      {...register('consignee.company')}
                      className='h-8 text-xs'
                      placeholder=''
                    />
                  </FormField>
                  <FormField label='ADDRESS *' required>
                    <Input
                      {...register('consignee.address1')}
                      className='h-8 text-xs'
                      placeholder='Address Line 1'
                    />
                  </FormField>
                  <Input
                    {...register('consignee.address2')}
                    className='h-8 text-xs'
                    placeholder='Address Line 2'
                  />
                  <Input
                    {...register('consignee.address3')}
                    className='h-8 text-xs'
                    placeholder='Address Line 3'
                  />
                  <div className='grid grid-cols-2 gap-2'>
                    <FormField label='CITY *' required>
                      <Input
                        {...register('consignee.city')}
                        className='h-8 text-xs'
                        placeholder=''
                      />
                    </FormField>
                    <FormField label='STATE'>
                      <Input
                        {...register('consignee.stateCounty')}
                        className='h-8 text-xs'
                        placeholder=''
                      />
                    </FormField>
                  </div>
                  <div className='grid grid-cols-2 gap-2'>
                    <FormField label='COUNTRY *' required>
                      <Controller
                        name='consignee.country'
                        control={control}
                        rules={{ required: 'Country is required' }}
                        render={({ field }) => (
                          <AsyncSearchableSelect
                            loadOptions={fetchCountries}
                            defaultOptions={masterData.countries
                              .slice(0, 20)
                              .map(c => ({ value: c._id, label: c.name }))}
                            value={field.value}
                            onValueChange={field.onChange}
                            placeholder='Search Country...'
                            className='h-8 text-xs'
                            initialLabel={
                              masterData.countries.find(
                                c => c._id === field.value
                              )?.name
                            }
                          />
                        )}
                      />
                    </FormField>
                    <FormField label='ZIP'>
                      <Input
                        {...register('consignee.postZipCode')}
                        className='h-8 text-xs'
                        placeholder=''
                      />
                    </FormField>
                  </div>
                  <FormField label='PHONE *' required>
                    <Input
                      {...register('consignee.phoneNumber')}
                      className='h-8 text-xs'
                      placeholder=''
                    />
                  </FormField>
                  <FormField label='EMAIL'>
                    <Input
                      type='email'
                      {...register('consignee.emailAddress')}
                      className='h-8 text-xs'
                      placeholder=''
                    />
                  </FormField>
                </div>
              </div>
              {/* Weight Summary (Moved from Right Column) */}
              <div className='mt-4 pt-4 border-t border-gray-200 dark:border-gray-700'>
                <div className='flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-200 border-b pb-2 mb-4'>
                  <Package className='h-4 w-4 text-blue-600' />
                  WEIGHT SUMMARY
                </div>

                <div className='grid grid-cols-2 gap-3 mb-4'>
                  <div className='bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-center'>
                    <div className='text-xs text-blue-600 dark:text-blue-400 font-medium'>
                      PCS
                    </div>
                    <Input
                      type='number'
                      min='1'
                      {...register('totalPieces', {
                        valueAsNumber: true,
                        min: 1,
                      })}
                      className='h-10 text-2xl font-bold text-center bg-transparent border-0 text-blue-900 dark:text-blue-100'
                    />
                  </div>
                  <div className='bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-center'>
                    <div className='text-xs text-green-600 dark:text-green-400 font-medium'>
                      Actual Weight
                    </div>
                    <div className='text-2xl font-bold text-green-900 dark:text-green-100'>
                      {watchedValues.totalActualWeight?.toFixed(2) || '0.00'}
                    </div>
                  </div>
                </div>

                <div className='grid grid-cols-2 gap-3'>
                  <div className='bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3 text-center'>
                    <div className='text-xs text-purple-600 dark:text-purple-400 font-medium'>
                      Volumetric Weight
                    </div>
                    <div className='text-2xl font-bold text-purple-900 dark:text-purple-100'>
                      {watchedValues.totalVolumetricWeight?.toFixed(2) ||
                        '0.00'}
                    </div>
                  </div>
                  <div className='bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3 text-center'>
                    <div className='text-xs text-orange-600 dark:text-orange-400 font-medium'>
                      Chargeable Weight
                    </div>
                    <div className='text-2xl font-bold text-orange-900 dark:text-orange-100'>
                      {watchedValues.totalChargeableWeight?.toFixed(2) ||
                        '0.00'}
                    </div>
                  </div>
                </div>

                <div className='mt-4'>
                  <div className='text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2'>
                    PACKAGE DETAILS
                  </div>

                  {/* Desktop Headers */}
                  <div className='hidden md:grid grid-cols-12 gap-3 px-2 py-1 bg-gray-100 dark:bg-gray-800 text-[10px] font-bold text-gray-500 uppercase border-b border-gray-200'>
                    <div className='col-span-1 whitespace-nowrap'>Box No.</div>
                    <div className='col-span-3 whitespace-nowrap'>
                      Actual Weight (Kg)
                    </div>
                    <div className='col-span-7 whitespace-nowrap'>
                      Dimensions (L × B × H) (cm)
                    </div>
                    <div className='col-span-1 whitespace-nowrap'>Action</div>
                  </div>

                  <div className='space-y-3 md:space-y-1'>
                    {weightFields.map((field, index) => (
                      <div
                        key={field.id}
                        className='bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-lg p-3 md:p-2 grid grid-cols-1 md:grid-cols-12 gap-3 items-end md:items-center shadow-sm'
                      >
                        {/* Box Number */}
                        <div className='col-span-1 md:col-span-1 flex items-center gap-2'>
                          <div className='h-6 w-6 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-xs font-bold text-gray-600 dark:text-gray-300'>
                            {index + 1}
                          </div>
                          <span className='md:hidden text-xs font-medium text-gray-500'>
                            Package #{index + 1}
                          </span>
                          <input
                            type='hidden'
                            {...register(
                              `weightDimensions.${index}.boxNo` as const,
                              { value: index + 1 }
                            )}
                          />
                        </div>

                        {/* Actual Weight */}
                        <div className='col-span-1 md:col-span-3'>
                          <Label className='md:hidden text-[10px] text-gray-500 uppercase tracking-wider mb-1 block'>
                            Actual Weight (Kg)
                          </Label>
                          <div className='relative'>
                            <Input
                              type='number'
                              step='0.01'
                              placeholder='Weight'
                              onWheel={e =>
                                (e.target as HTMLInputElement).blur()
                              }
                              {...register(
                                `weightDimensions.${index}.actualWeight` as const,
                                { valueAsNumber: true }
                              )}
                              className='h-8 text-sm text-right pr-2 font-medium'
                            />
                            <div className='hidden md:block absolute right-8 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none'>
                              kg
                            </div>
                          </div>
                        </div>

                        {/* Dimensions */}
                        <div className='col-span-1 md:col-span-7'>
                          <Label className='md:hidden text-[10px] text-gray-500 uppercase tracking-wider mb-1 block'>
                            Dimensions (L × B × H) (cm)
                          </Label>
                          <div className='flex items-center gap-2'>
                            <div className='relative flex-1'>
                              <Input
                                type='number'
                                placeholder='L'
                                onWheel={e =>
                                  (e.target as HTMLInputElement).blur()
                                }
                                {...register(
                                  `weightDimensions.${index}.length` as const,
                                  { valueAsNumber: true }
                                )}
                                className='h-8 text-sm text-center'
                              />
                            </div>
                            <span className='text-gray-300 text-xs'>×</span>
                            <div className='relative flex-1'>
                              <Input
                                type='number'
                                placeholder='B'
                                onWheel={e =>
                                  (e.target as HTMLInputElement).blur()
                                }
                                {...register(
                                  `weightDimensions.${index}.breadth` as const,
                                  { valueAsNumber: true }
                                )}
                                className='h-8 text-sm text-center'
                              />
                            </div>
                            <span className='text-gray-300 text-xs'>×</span>
                            <div className='relative flex-1'>
                              <Input
                                type='number'
                                placeholder='H'
                                onWheel={e =>
                                  (e.target as HTMLInputElement).blur()
                                }
                                {...register(
                                  `weightDimensions.${index}.height` as const,
                                  { valueAsNumber: true }
                                )}
                                className='h-8 text-sm text-center'
                              />
                            </div>
                          </div>
                        </div>

                        {/* Delete Action */}
                        <div className='col-span-1 md:col-span-1 flex justify-end md:justify-center'>
                          {weightFields.length > 1 && (
                            <Button
                              type='button'
                              variant='ghost'
                              size='sm'
                              onClick={() => removeWeight(index)}
                              className='h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-full'
                            >
                              <Trash2 className='h-4 w-4' />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    onClick={() =>
                      appendWeight({
                        parcelNo: '',
                        boxNo: weightFields.length + 1,
                        actualWeight: 0,
                        length: 0,
                        breadth: 0,
                        height: 0,
                        volumetricWeight: 0,
                        chargeableWeight: 0,
                      })
                    }
                    className='w-full text-xs mt-3 h-9 border-dashed'
                  >
                    <Plus className='h-3 w-3 mr-1' />
                    Add Package
                  </Button>
                </div>

                {/* Extra Weights */}
                <div className='grid grid-cols-2 gap-2 mt-4 pt-3 border-t'>
                  <FormField label='Consigner Weight'>
                    <Input
                      type='number'
                      step='0.01'
                      {...register('consignorWeight', { valueAsNumber: true })}
                      className='h-8 text-xs'
                      placeholder='0'
                    />
                  </FormField>
                  <FormField label='ADD Weight'>
                    <Input
                      type='number'
                      step='0.01'
                      {...register('addWeight', { valueAsNumber: true })}
                      className='h-8 text-xs'
                      placeholder='0'
                    />
                  </FormField>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Shipment Invoice Section */}
        <div className='bg-white dark:bg-gray-800 border border-t-0 border-gray-200 dark:border-gray-700 p-4'>
          <div className='flex items-center gap-2 mb-4'>
            <input
              type='checkbox'
              id='createShipmentInvoice'
              className='rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4'
              {...register('createShipmentInvoice')}
            />
            <label
              htmlFor='createShipmentInvoice'
              className='text-sm font-bold text-gray-700 dark:text-gray-300'
            >
              CREATE SHIPMENT INVOICE?
            </label>
          </div>

          {createShipmentInvoice && (
            <div className='space-y-4 border-t border-gray-100 pt-4'>
              <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                <FormField label='INVOICE TYPE ?'>
                  <Controller
                    name='shipmentInvoice.invoiceType'
                    control={control}
                    render={({ field }) => (
                      <SearchableSelect
                        options={[
                          {
                            value: 'PROFORMA_INVOICE',
                            label: 'PROFORMA INVOICE',
                          },
                          {
                            value: 'COMMERCIAL_INVOICE',
                            label: 'COMMERCIAL INVOICE',
                          },
                        ]}
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder='Select...'
                        className='h-8 text-xs'
                      />
                    )}
                  />
                </FormField>
                <FormField label='CURRENCY'>
                  <Controller
                    name='shipmentInvoice.currency'
                    control={control}
                    render={({ field }) => (
                      <AsyncSearchableSelect
                        loadOptions={fetchCurrencies}
                        defaultOptions={masterData.currencies.map(c => ({
                          value: c._id,
                          label: `${c.symbol ? `${c.symbol} ` : ''}${c.name} (${c.code})`,
                        }))}
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder='Select...'
                        className='h-8 text-xs'
                        initialLabel={
                          masterData.currencies.find(c => c._id === field.value)
                            ?.code
                        }
                      />
                    )}
                  />
                </FormField>
                <FormField label='INCOTERMS'>
                  <Controller
                    name='shipmentInvoice.incoterms'
                    control={control}
                    render={({ field }) => (
                      <SearchableSelect
                        options={masterData.incoterms.map(i => ({
                          value: i.code,
                          label: i.code,
                        }))}
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder='Select...'
                        className='h-8 text-xs'
                      />
                    )}
                  />
                </FormField>
              </div>

              <FormField label='NOTE'>
                <Controller
                  name='shipmentInvoice.note'
                  control={control}
                  render={({ field }) => (
                    <SearchableSelect
                      options={[
                        {
                          value: 'gift from person to person',
                          label: 'Gift from person to person',
                        },
                        { value: 'Sample', label: 'Sample' },
                        { value: 'Gift', label: 'Gift' },
                      ]}
                      value={field.value}
                      onValueChange={field.onChange}
                      placeholder='Select...'
                      className='h-8 text-xs'
                    />
                  )}
                />
              </FormField>
              <FormField label='REMARKS'>
                <Textarea
                  {...register('shipmentInvoice.remarks')}
                  className='text-xs resize-none'
                  placeholder='Invoice remarks...'
                  rows={2}
                />
              </FormField>

              {/* Invoice Items Section */}
              <div>
                <div className='bg-slate-700 text-white px-3 py-2 text-xs font-bold uppercase mb-2 rounded-t-md'>
                  Shipment Invoice Items
                </div>

                {/* Desktop Headers */}
                <div className='hidden md:grid grid-cols-12 gap-2 px-2 py-1 bg-gray-100 dark:bg-gray-800 text-[10px] font-bold text-gray-500 uppercase border-b border-gray-200'>
                  <div className='col-span-1'>Box No.</div>
                  <div className='col-span-1'>Sr. No.</div>
                  <div className='col-span-3'>Description</div>
                  <div className='col-span-1'>HS Code</div>
                  <div className='col-span-1'>Unit Type</div>
                  <div className='col-span-1 text-center'>Quantity</div>
                  <div className='col-span-1 text-center'>Unit Weight</div>
                  <div className='col-span-1 text-center'>IGST</div>
                  <div className='col-span-1 text-right'>Unit Rates</div>
                  <div className='col-span-1 text-right'>Amount</div>
                </div>

                <div className='space-y-3 md:space-y-1'>
                  {invoiceFields.map((field, index) => (
                    <div
                      key={field.id}
                      className='bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-lg p-3 md:p-2 grid grid-cols-1 md:grid-cols-12 gap-3 items-end md:items-center shadow-sm'
                    >
                      {/* Mobile Header: Item Number and Remove */}
                      <div className='md:hidden flex justify-between items-center mb-1 border-b border-gray-50 dark:border-gray-700 pb-2 col-span-1'>
                        <span className='text-xs font-semibold text-gray-500'>
                          Item #{index + 1}
                        </span>
                        {invoiceFields.length > 1 && (
                          <Button
                            type='button'
                            variant='ghost'
                            size='sm'
                            onClick={() => removeInvoiceItem(index)}
                            className='h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50'
                          >
                            <Trash2 className='h-3 w-3' />
                          </Button>
                        )}
                      </div>

                      {/* Box No */}
                      <div className='col-span-1'>
                        <Label className='md:hidden text-[10px] text-gray-500 uppercase tracking-wider mb-1 block'>
                          Box No.
                        </Label>
                        <Input
                          {...register(
                            `shipmentInvoice.lineItems.${index}.boxNo`
                          )}
                          placeholder='Box'
                          className='h-8 text-sm'
                        />
                      </div>

                      {/* Sr No */}
                      <div className='col-span-1'>
                        <Label className='md:hidden text-[10px] text-gray-500 uppercase tracking-wider mb-1 block'>
                          Sr. No.
                        </Label>
                        <Input
                          type='number'
                          {...register(
                            `shipmentInvoice.lineItems.${index}.srNo`,
                            { valueAsNumber: true }
                          )}
                          placeholder='Sr'
                          className='h-8 text-sm'
                        />
                      </div>

                      {/* Description */}
                      <div className='col-span-1 md:col-span-3'>
                        <Label className='md:hidden text-[10px] text-gray-500 uppercase tracking-wider mb-1 block'>
                          Description
                        </Label>
                        <Input
                          {...register(
                            `shipmentInvoice.lineItems.${index}.description`
                          )}
                          placeholder='Description'
                          className='h-8 text-sm'
                        />
                      </div>

                      {/* HS Code */}
                      <div className='col-span-1 md:col-span-1'>
                        <Label className='md:hidden text-[10px] text-gray-500 uppercase tracking-wider mb-1 block'>
                          HS Code
                        </Label>
                        <Input
                          {...register(
                            `shipmentInvoice.lineItems.${index}.hsCode`
                          )}
                          placeholder='HS'
                          className='h-8 text-sm'
                        />
                      </div>

                      {/* Unit Type */}
                      <div className='col-span-1 md:col-span-1'>
                        <Label className='md:hidden text-[10px] text-gray-500 uppercase tracking-wider mb-1 block'>
                          Unit Type
                        </Label>
                        <Controller
                          name={`shipmentInvoice.lineItems.${index}.unitType`}
                          control={control}
                          render={({ field }) => (
                            <select
                              className='h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                              value={field.value}
                              onChange={field.onChange}
                            >
                              <option value='PCS'>PCS</option>
                              <option value='BOX'>BOX</option>
                              <option value='SET'>SET</option>
                              <option value='KG'>KG</option>
                            </select>
                          )}
                        />
                      </div>

                      {/* Quantity */}
                      <div className='col-span-1'>
                        <Label className='md:hidden text-[10px] text-gray-500 uppercase tracking-wider mb-1 block'>
                          Quantity
                        </Label>
                        <Input
                          type='number'
                          placeholder='Qty'
                          onWheel={e => (e.target as HTMLInputElement).blur()}
                          {...register(
                            `shipmentInvoice.lineItems.${index}.quantity`,
                            { valueAsNumber: true }
                          )}
                          className='h-8 text-sm text-center'
                        />
                      </div>

                      {/* Unit Weight */}
                      <div className='col-span-1'>
                        <Label className='md:hidden text-[10px] text-gray-500 uppercase tracking-wider mb-1 block'>
                          Unit Weight
                        </Label>
                        <Input
                          type='number'
                          step='0.01'
                          placeholder='Wt'
                          onWheel={e => (e.target as HTMLInputElement).blur()}
                          {...register(
                            `shipmentInvoice.lineItems.${index}.unitWeight`,
                            { valueAsNumber: true }
                          )}
                          className='h-8 text-sm text-center'
                        />
                      </div>

                      {/* IGST */}
                      <div className='col-span-1'>
                        <Label className='md:hidden text-[10px] text-gray-500 uppercase tracking-wider mb-1 block'>
                          IGST
                        </Label>
                        <Input
                          type='number'
                          step='0.01'
                          placeholder='%'
                          onWheel={e => (e.target as HTMLInputElement).blur()}
                          {...register(
                            `shipmentInvoice.lineItems.${index}.igst`,
                            { valueAsNumber: true }
                          )}
                          className='h-8 text-sm text-center'
                        />
                      </div>

                      {/* Unit Rate */}
                      <div className='col-span-1'>
                        <Label className='md:hidden text-[10px] text-gray-500 uppercase tracking-wider mb-1 block'>
                          Unit Rates
                        </Label>
                        <Input
                          type='number'
                          step='0.01'
                          placeholder='Rate'
                          onWheel={e => (e.target as HTMLInputElement).blur()}
                          {...register(
                            `shipmentInvoice.lineItems.${index}.unitRate`,
                            { valueAsNumber: true }
                          )}
                          className='h-8 text-sm text-right'
                        />
                      </div>

                      {/* Amount */}
                      <div className='col-span-1 flex items-center gap-1'>
                        <Label className='md:hidden text-[10px] text-gray-500 uppercase tracking-wider mb-1 block'>
                          Amount
                        </Label>
                        <Input
                          type='number'
                          readOnly
                          value={(
                            (watch(
                              `shipmentInvoice.lineItems.${index}.quantity`
                            ) || 0) *
                            (watch(
                              `shipmentInvoice.lineItems.${index}.unitRate`
                            ) || 0)
                          ).toFixed(2)}
                          className='h-8 text-sm text-right bg-gray-50 dark:bg-gray-900 font-medium flex-grow'
                        />
                        {invoiceFields.length > 1 && (
                          <Button
                            type='button'
                            variant='ghost'
                            size='sm'
                            onClick={() => removeInvoiceItem(index)}
                            className='h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-full hidden md:flex flex-shrink-0'
                          >
                            <Trash2 className='h-4 w-4' />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <Button
                  type='button'
                  size='sm'
                  variant='outline'
                  className='mt-3 text-xs h-9 w-full md:w-auto bg-green-600 text-white hover:bg-green-700 border-green-600 hover:text-white shadow-sm'
                  onClick={() =>
                    appendInvoiceItem({
                      boxNo: '1',
                      srNo: invoiceFields.length + 1,
                      description: '',
                      hsCode: '',
                      unitType: 'PCS',
                      quantity: 1,
                      unitWeight: 0,
                      igst: 0,
                      unitRate: 0,
                      amount: 0,
                    })
                  }
                >
                  <Plus className='h-3 w-3 mr-1' />
                  ADD ITEM
                </Button>
              </div>
            </div>
          )}
        </div>
      </>
    );
  };

  const handleSaveSalesBilling = async () => {
    setIsSavingSalesBilling(true);
    try {
      const data = watch();

      // Get currency ID - ensure we have a valid currency
      const currencyId = data.salesBilling.salesCurrency || data.currency;
      if (!currencyId) {
        toast({
          title: 'Error',
          description: 'Please select a currency for sales billing',
          variant: 'destructive',
        });
        setIsSavingSalesBilling(false);
        return;
      }

      const salesBillingData = {
        salesBilling: {
          // Basic
          salesCurrency: data.salesBilling.salesCurrency || data.currency,
          currency: currencyId, // Required field
          gstType: data.salesBilling.gstType || 'IGST',
          gstApplicable: data.salesBilling.gstApplicable !== false,

          // Freight
          freight: data.salesBilling.freight || 0,
          freightPerKg: data.salesBilling.freightPerKg || 0,
          searchCharge: data.salesBilling.searchCharge || '',

          // FSC
          fscPercent: data.salesBilling.fscPercent || 0,
          fsc: data.salesBilling.fsc || 0,

          // Individual Surcharges
          addressCorrection: {
            enabled: data.salesBilling.addressCorrection?.enabled || false,
            amount: data.salesBilling.addressCorrection?.amount || 0,
            quantity: data.salesBilling.addressCorrection?.quantity || 0,
          },
          clearanceCharge: {
            enabled: data.salesBilling.clearanceCharge?.enabled || false,
            amount: data.salesBilling.clearanceCharge?.amount || 0,
            quantity: data.salesBilling.clearanceCharge?.quantity || 0,
          },
          elevatedRiskSurcharge: {
            enabled: data.salesBilling.elevatedRiskSurcharge?.enabled || false,
            amount: data.salesBilling.elevatedRiskSurcharge?.amount || 0,
            quantity: data.salesBilling.elevatedRiskSurcharge?.quantity || 0,
          },
          ess: {
            enabled: data.salesBilling.ess?.enabled || false,
            amount: data.salesBilling.ess?.amount || 0,
            quantity: data.salesBilling.ess?.quantity || 0,
          },
          imitation: {
            enabled: data.salesBilling.imitation?.enabled || false,
            amount: data.salesBilling.imitation?.amount || 0,
            quantity: data.salesBilling.imitation?.quantity || 0,
          },
          medicineHandlingCharges: {
            enabled:
              data.salesBilling.medicineHandlingCharges?.enabled || false,
            amount: data.salesBilling.medicineHandlingCharges?.amount || 0,
            quantity: data.salesBilling.medicineHandlingCharges?.quantity || 0,
          },
          nonStackablePallet: {
            enabled: data.salesBilling.nonStackablePallet?.enabled || false,
            amount: data.salesBilling.nonStackablePallet?.amount || 0,
            quantity: data.salesBilling.nonStackablePallet?.quantity || 0,
          },
          oversizeOverweightSurcharge: {
            enabled:
              data.salesBilling.oversizeOverweightSurcharge?.enabled || false,
            amount: data.salesBilling.oversizeOverweightSurcharge?.amount || 0,
            quantity:
              data.salesBilling.oversizeOverweightSurcharge?.quantity || 0,
          },
          remoteAreaCharges: {
            enabled: data.salesBilling.remoteAreaCharges?.enabled || false,
            amount: data.salesBilling.remoteAreaCharges?.amount || 0,
            quantity: data.salesBilling.remoteAreaCharges?.quantity || 0,
          },
          restrictedDestinationSurcharge: {
            enabled:
              data.salesBilling.restrictedDestinationSurcharge?.enabled ||
              false,
            amount:
              data.salesBilling.restrictedDestinationSurcharge?.amount || 0,
            quantity:
              data.salesBilling.restrictedDestinationSurcharge?.quantity || 0,
          },

          // Surcharges Total
          totalOtherCharges: data.salesBilling.totalOtherCharges || 0,
          adjustmentAmount: data.salesBilling.adjustmentAmount || 0,

          // Discount
          discountPercent: data.salesBilling.discountPercent || 0,
          discountAmount: data.salesBilling.discountAmount || 0,
          totalDiscount: data.salesBilling.totalDiscount || 0,
          freightAfterDiscount: data.salesBilling.freightAfterDiscount || 0,

          // Tax Calculations
          subtotal: data.salesBilling.subtotal || 0,
          nonTaxableAmount: data.salesBilling.nonTaxableAmount || 0,
          taxableAmount: data.salesBilling.taxableAmount || 0,
          gstPercent: data.salesBilling.gstPercent || 18,
          cgst: data.salesBilling.cgst || 0,
          sgst: data.salesBilling.sgst || 0,
          grandTotal: data.salesBilling.grandTotal || 0,

          // Legacy fields
          totalCharges: data.salesBilling.totalCharges || 0,
          gstAmount: data.salesBilling.gstAmount || 0,
          finalAmount:
            data.salesBilling.finalAmount || data.salesBilling.grandTotal || 0,

          // Payment Details
          paidAmount: data.salesBilling.paidAmount || 0,
          balanceAmount: data.salesBilling.balanceAmount || 0,
          salesInvoiceDate: data.salesBilling.salesInvoiceDate || undefined,
          salesInvoiceNumber: data.salesBilling.salesInvoiceNumber || undefined,
          salesInvoiceRemarks:
            data.salesBilling.salesInvoiceRemarks || undefined,
        },
      };

      console.log('Saving sales billing:', salesBillingData);

      if (awbNumber) {
        // Update existing AWB
        await awbService.updateAWB(awbNumber, salesBillingData as any);
        toast({
          title: 'Success',
          description: 'Sales billing saved successfully',
        });
      } else {
        // Create AWB with billing data
        const data = watch();
        const submitData = {
          customer: data.customer,
          company: data.company,
          destination: data.destination,
          product: data.product,
          service: data.service,
          bookingDate: new Date(data.bookingDate),
          bookingTime: data.bookingTime,
          currency: data.currency,
          paymentMethod: data.paymentMethod as any,
          salesBilling: salesBillingData.salesBilling,
        };
        const newAWB = await awbService.createAWB(submitData as any);
        setAwbNumber(newAWB.awbNumber);
        toast({
          title: 'Success',
          description: `AWB ${newAWB.awbNumber} created with sales billing`,
        });
      }
    } catch (error: any) {
      console.error('Error saving sales billing:', error);
      toast({
        title: 'Error',
        description:
          error.response?.data?.error?.message ||
          'Failed to save sales billing',
        variant: 'destructive',
      });
    } finally {
      setIsSavingSalesBilling(false);
    }
  };

  const handleSavePurchaseBilling = async () => {
    setIsSavingPurchaseBilling(true);
    try {
      const data = watch();

      // Get currency ID - ensure we have a valid currency
      const currencyId = data.purchaseBilling.currency || data.currency;
      if (!currencyId) {
        toast({
          title: 'Error',
          description: 'Please select a currency for purchase billing',
          variant: 'destructive',
        });
        setIsSavingPurchaseBilling(false);
        return;
      }

      const purchaseBillingData = {
        purchaseBilling: {
          // Basic
          company: data.purchaseBilling.company || data.company || undefined,
          currency: currencyId, // Required field
          gstType: data.purchaseBilling.gstType || 'IGST',
          gstApplicable: data.purchaseBilling.gstApplicable !== false,

          // Freight
          freight: data.purchaseBilling.freight || 0,
          freightPerKg: data.purchaseBilling.freightPerKg || 0,
          searchCharge: data.purchaseBilling.searchCharge || '',

          // FSC
          fscPercent: data.purchaseBilling.fscPercent || 0,
          fsc: data.purchaseBilling.fsc || 0,

          // Individual Surcharges
          addressCorrection: {
            enabled: data.purchaseBilling.addressCorrection?.enabled || false,
            amount: data.purchaseBilling.addressCorrection?.amount || 0,
            quantity: data.purchaseBilling.addressCorrection?.quantity || 0,
          },
          clearanceCharge: {
            enabled: data.purchaseBilling.clearanceCharge?.enabled || false,
            amount: data.purchaseBilling.clearanceCharge?.amount || 0,
            quantity: data.purchaseBilling.clearanceCharge?.quantity || 0,
          },
          elevatedRiskSurcharge: {
            enabled:
              data.purchaseBilling.elevatedRiskSurcharge?.enabled || false,
            amount: data.purchaseBilling.elevatedRiskSurcharge?.amount || 0,
            quantity: data.purchaseBilling.elevatedRiskSurcharge?.quantity || 0,
          },
          ess: {
            enabled: data.purchaseBilling.ess?.enabled || false,
            amount: data.purchaseBilling.ess?.amount || 0,
            quantity: data.purchaseBilling.ess?.quantity || 0,
          },
          imitation: {
            enabled: data.purchaseBilling.imitation?.enabled || false,
            amount: data.purchaseBilling.imitation?.amount || 0,
            quantity: data.purchaseBilling.imitation?.quantity || 0,
          },
          medicineHandlingCharges: {
            enabled:
              data.purchaseBilling.medicineHandlingCharges?.enabled || false,
            amount: data.purchaseBilling.medicineHandlingCharges?.amount || 0,
            quantity:
              data.purchaseBilling.medicineHandlingCharges?.quantity || 0,
          },
          nonStackablePallet: {
            enabled: data.purchaseBilling.nonStackablePallet?.enabled || false,
            amount: data.purchaseBilling.nonStackablePallet?.amount || 0,
            quantity: data.purchaseBilling.nonStackablePallet?.quantity || 0,
          },
          oversizeOverweightSurcharge: {
            enabled:
              data.purchaseBilling.oversizeOverweightSurcharge?.enabled ||
              false,
            amount:
              data.purchaseBilling.oversizeOverweightSurcharge?.amount || 0,
            quantity:
              data.purchaseBilling.oversizeOverweightSurcharge?.quantity || 0,
          },
          remoteAreaCharges: {
            enabled: data.purchaseBilling.remoteAreaCharges?.enabled || false,
            amount: data.purchaseBilling.remoteAreaCharges?.amount || 0,
            quantity: data.purchaseBilling.remoteAreaCharges?.quantity || 0,
          },
          restrictedDestinationSurcharge: {
            enabled:
              data.purchaseBilling.restrictedDestinationSurcharge?.enabled ||
              false,
            amount:
              data.purchaseBilling.restrictedDestinationSurcharge?.amount || 0,
            quantity:
              data.purchaseBilling.restrictedDestinationSurcharge?.quantity ||
              0,
          },

          // Surcharges & Totals
          totalOtherCharges: data.purchaseBilling.totalOtherCharges || 0,

          // Discount
          discountPercent: data.purchaseBilling.discountPercent || 0,
          discountAmount: data.purchaseBilling.discountAmount || 0,
          totalDiscount: data.purchaseBilling.totalDiscount || 0,
          freightAfterDiscount: data.purchaseBilling.freightAfterDiscount || 0,

          // Tax Calculations
          subtotal: data.purchaseBilling.subtotal || 0,
          nonTaxableAmount: data.purchaseBilling.nonTaxableAmount || 0,
          taxableAmount: data.purchaseBilling.taxableAmount || 0,
          gstPercent: data.purchaseBilling.gstPercent || 18,
          cgst: data.purchaseBilling.cgst || 0,
          sgst: data.purchaseBilling.sgst || 0,
          grandTotal: data.purchaseBilling.grandTotal || 0,

          // Legacy fields
          totalCharges: data.purchaseBilling.totalCharges || 0,
          gstAmount: data.purchaseBilling.gstAmount || 0,
          finalAmount:
            data.purchaseBilling.finalAmount ||
            data.purchaseBilling.grandTotal ||
            0,

          // Vendor Contract Details
          cftId: data.purchaseBilling.cftId || undefined,
          cftValue: data.purchaseBilling.cftValue || 0,
          vendorContractId: data.purchaseBilling.vendorContractId || undefined,
          vendorTat: data.purchaseBilling.vendorTat || undefined,

          // Vendor Invoices
          pastVendorInvoice:
            data.purchaseBilling.pastVendorInvoice || undefined,
          vendorInvoice1: data.purchaseBilling.vendorInvoice1 || undefined,
          invoiceRemarks1: data.purchaseBilling.invoiceRemarks1 || undefined,
          vendorInvoice2: data.purchaseBilling.vendorInvoice2 || undefined,
          invoiceRemarks2: data.purchaseBilling.invoiceRemarks2 || undefined,
        },
      };

      console.log('Saving purchase billing:', purchaseBillingData);

      if (awbNumber) {
        // Update existing AWB
        await awbService.updateAWB(awbNumber, purchaseBillingData as any);
        toast({
          title: 'Success',
          description: 'Purchase billing saved successfully',
        });
      } else {
        // Create AWB with billing data
        const data = watch();
        const submitData = {
          customer: data.customer,
          company: data.company,
          destination: data.destination,
          product: data.product,
          service: data.service,
          bookingDate: new Date(data.bookingDate),
          bookingTime: data.bookingTime,
          currency: data.currency,
          paymentMethod: data.paymentMethod as any,
          purchaseBilling: purchaseBillingData.purchaseBilling,
        };
        const newAWB = await awbService.createAWB(submitData as any);
        setAwbNumber(newAWB.awbNumber);
        toast({
          title: 'Success',
          description: `AWB ${newAWB.awbNumber} created with purchase billing`,
        });
      }
    } catch (error: any) {
      console.error('Error saving purchase billing:', error);
      toast({
        title: 'Error',
        description:
          error.response?.data?.error?.message ||
          'Failed to save purchase billing',
        variant: 'destructive',
      });
    } finally {
      setIsSavingPurchaseBilling(false);
    }
  };

  // ============================================================================
  // HELPER: SURCHARGE ROW RENDER FUNCTIONS (using regular functions instead of components)
  // ============================================================================

  const renderSurchargeRow = (
    name: string,
    label: string,
    showQuantity = true
  ) => (
    <div key={name} className='flex items-center gap-2 py-1'>
      <Controller
        name={`salesBilling.${name}.enabled` as any}
        control={control}
        render={({ field }) => (
          <input
            type='checkbox'
            checked={field.value}
            onChange={field.onChange}
            className='h-4 w-4'
          />
        )}
      />
      <span className='text-xs text-gray-700 dark:text-gray-300 w-44 truncate'>
        {label}
      </span>
      <Input
        type='number'
        step='0.01'
        {...register(`salesBilling.${name}.amount` as any, {
          valueAsNumber: true,
        })}
        className='h-7 w-20 text-xs'
        placeholder='0'
      />
      {showQuantity && (
        <Input
          type='number'
          step='1'
          {...register(`salesBilling.${name}.quantity` as any, {
            valueAsNumber: true,
          })}
          className='h-7 w-16 text-xs'
          placeholder='0'
        />
      )}
      <span className='text-xs text-gray-500'>₹</span>
    </div>
  );

  const renderPurchaseSurchargeRow = (
    name: string,
    label: string,
    showQuantity = true
  ) => (
    <div key={name} className='flex items-center gap-2 py-1'>
      <Controller
        name={`purchaseBilling.${name}.enabled` as any}
        control={control}
        render={({ field }) => (
          <input
            type='checkbox'
            checked={field.value}
            onChange={field.onChange}
            className='h-4 w-4'
          />
        )}
      />
      <span className='text-xs text-gray-700 dark:text-gray-300 w-44 truncate'>
        {label}
      </span>
      <Input
        type='number'
        step='0.01'
        {...register(`purchaseBilling.${name}.amount` as any, {
          valueAsNumber: true,
        })}
        className='h-7 w-20 text-xs'
        placeholder='0'
      />
      {showQuantity && (
        <Input
          type='number'
          step='1'
          {...register(`purchaseBilling.${name}.quantity` as any, {
            valueAsNumber: true,
          })}
          className='h-7 w-16 text-xs'
          placeholder='0'
        />
      )}
      <span className='text-xs text-gray-500'>₹</span>
    </div>
  );

  // ============================================================================
  // RENDER: SALES BILLING TAB
  // ============================================================================

  const renderSalesBillingTab = () => (
    <div className='bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'>
      {/* Header */}
      <div className='flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700'>
        <h2 className='text-sm font-semibold text-gray-800 dark:text-gray-200'>
          AWB SALES BILLING
        </h2>
        <div className='flex items-center gap-2'>
          <Button
            type='button'
            size='sm'
            onClick={handleSaveSalesBilling}
            disabled={isSavingSalesBilling}
            className='bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-4'
          >
            {isSavingSalesBilling ? (
              <RefreshCw className='h-3 w-3 animate-spin mr-1' />
            ) : (
              <Save className='h-3 w-3 mr-1' />
            )}
            {isSavingSalesBilling ? 'SAVING...' : 'SAVE BILLING'}
          </Button>
        </div>
      </div>

      {/* Main Content - 3 Column Layout */}
      <div className='grid grid-cols-1 lg:grid-cols-12 gap-0'>
        {/* LEFT COLUMN: AWB Information (Summary) */}
        <div className='lg:col-span-4 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700 p-4 space-y-3'>
          <h3 className='text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider border-b pb-2'>
            Air Waybill Information
          </h3>

          {/* AWB Number */}
          <FormField label='AWB NUMBER'>
            <Input
              value={watch('awbNumber') || ''}
              disabled
              className='h-8 text-xs bg-gray-50'
            />
          </FormField>

          {/* Customer */}
          <div className='grid grid-cols-2 gap-2'>
            <FormField label='CUSTOMER'>
              <Input
                value={
                  masterData.customers.find(c => c._id === watch('customer'))
                    ?.name || ''
                }
                disabled
                className='h-8 text-xs bg-gray-50'
              />
            </FormField>
            <FormField label='CODE'>
              <Input
                value={
                  masterData.customers.find(c => c._id === watch('customer'))
                    ?.code || ''
                }
                disabled
                className='h-8 text-xs bg-gray-50'
              />
            </FormField>
          </div>

          {/* Company */}
          <FormField label='COMPANY'>
            <Input
              value={
                masterData.companies.find(c => c._id === watch('company'))
                  ?.name || ''
              }
              disabled
              className='h-8 text-xs bg-gray-50'
            />
          </FormField>

          {/* Origin / Destination */}
          <div className='grid grid-cols-2 gap-2'>
            <FormField label='ORIGIN'>
              <Input
                value={
                  masterData.countries.find(c => c._id === watch('origin'))
                    ?.name || ''
                }
                disabled
                className='h-8 text-xs bg-gray-50'
              />
            </FormField>
            <FormField label='DESTINATION'>
              <Input
                value={
                  masterData.countries.find(c => c._id === watch('destination'))
                    ?.name || ''
                }
                disabled
                className='h-8 text-xs bg-gray-50'
              />
            </FormField>
          </div>

          {/* Product & Service */}
          <FormField label='PRODUCT'>
            <Input
              value={
                masterData.products.find(p => p._id === watch('product'))
                  ?.name || ''
              }
              disabled
              className='h-8 text-xs bg-gray-50'
            />
          </FormField>
          <FormField label='SERVICE'>
            <Input
              value={
                masterData.services.find(s => s._id === watch('service'))
                  ?.name || ''
              }
              disabled
              className='h-8 text-xs bg-gray-50'
            />
          </FormField>

          {/* Vendor */}
          <FormField label='VENDOR'>
            <Input
              value={
                masterData.vendors.find(v => v._id === watch('vendor'))?.name ||
                ''
              }
              disabled
              className='h-8 text-xs bg-gray-50'
            />
          </FormField>

          {/* Booking Date / Time */}
          <div className='grid grid-cols-2 gap-2'>
            <FormField label='BOOKING DATE'>
              <Input
                value={watch('bookingDate') || ''}
                disabled
                className='h-8 text-xs bg-gray-50'
              />
            </FormField>
            <FormField label='BOOKING TIME'>
              <Input
                value={watch('bookingTime') || ''}
                disabled
                className='h-8 text-xs bg-gray-50'
              />
            </FormField>
          </div>

          {/* Shipment Value / Currency */}
          <div className='grid grid-cols-2 gap-2'>
            <FormField label='SHIPMENT VALUE'>
              <Input
                value={watch('shipmentValue') || 0}
                disabled
                className='h-8 text-xs bg-gray-50'
              />
            </FormField>
            <FormField label='CURRENCY'>
              <Input
                value={
                  masterData.currencies.find(c => c._id === watch('currency'))
                    ?.code || 'INR'
                }
                disabled
                className='h-8 text-xs bg-gray-50'
              />
            </FormField>
          </div>

          {/* Remarks */}
          <FormField label='REMARKS'>
            <Textarea
              value={watch('remarks') || ''}
              disabled
              className='h-12 text-xs resize-none bg-gray-50'
            />
          </FormField>
        </div>

        {/* MIDDLE COLUMN: Sales Billing */}
        <div className='lg:col-span-5 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700 p-4'>
          <div className='border border-gray-300 dark:border-gray-600 p-4'>
            <h3 className='text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4'>
              SALES BILLING
            </h3>

            {/* Sales Currency & GST Type */}
            <div className='grid grid-cols-2 gap-4 mb-4'>
              <FormField label='SALES CURRENCY'>
                <Controller
                  name='salesBilling.salesCurrency'
                  control={control}
                  render={({ field }) => (
                    <AsyncSearchableSelect
                      loadOptions={fetchCurrencies}
                      defaultOptions={masterData.currencies.map(c => ({
                        value: c._id,
                        label: `${c.symbol ? `${c.symbol} ` : ''}${c.name} (${c.code})`,
                      }))}
                      value={field.value || watch('currency')}
                      onValueChange={field.onChange}
                      placeholder='Select...'
                      className='h-8 text-xs'
                      initialLabel={
                        masterData.currencies.find(
                          c => c._id === (field.value || watch('currency'))
                        )?.code
                      }
                    />
                  )}
                />
              </FormField>
              <FormField label='GST TYPE'>
                <Controller
                  name='salesBilling.gstType'
                  control={control}
                  render={({ field }) => (
                    <SearchableSelect
                      value={field.value}
                      onValueChange={field.onChange}
                      placeholder='GST'
                      options={GST_TYPES}
                      className='h-8 text-xs'
                    />
                  )}
                />
              </FormField>
            </div>

            {/* GST Applicable */}
            <div className='flex items-center gap-4 mb-4'>
              <Controller
                name='salesBilling.gstApplicable'
                control={control}
                render={({ field }) => (
                  <label className='flex items-center gap-2 text-xs'>
                    <input
                      type='checkbox'
                      checked={field.value}
                      onChange={field.onChange}
                      className='h-4 w-4'
                    />
                    GST APPLICABLE
                  </label>
                )}
              />
            </div>

            {/* Freight */}
            <div className='grid grid-cols-3 gap-2 mb-3'>
              <FormField label='FREIGHT'>
                <Input
                  type='number'
                  step='0.01'
                  {...register('salesBilling.freight', { valueAsNumber: true })}
                  className='h-8 text-xs'
                  placeholder='0'
                />
              </FormField>
              <div className='flex items-end mb-1'>
                <Controller
                  name='salesBilling.freightEditable'
                  control={control}
                  render={({ field }) => (
                    <label className='flex items-center gap-1 text-xs'>
                      <input
                        type='checkbox'
                        checked={field.value}
                        onChange={field.onChange}
                        className='h-3 w-3'
                      />
                      EDIT AMOUNT
                    </label>
                  )}
                />
              </div>
            </div>

            {/* Freight Per KG */}
            <FormField label='FREIGHT PER KG'>
              <Input
                type='number'
                step='0.01'
                {...register('salesBilling.freightPerKg', {
                  valueAsNumber: true,
                })}
                className='h-8 text-xs w-40'
                placeholder='0'
              />
            </FormField>

            {/* Search Charge */}
            <FormField label='SEARCH CHARGE'>
              <Input
                {...register('salesBilling.searchCharge')}
                className='h-8 text-xs w-48'
                placeholder=''
              />
            </FormField>

            {/* Surcharges */}
            <div className='space-y-1 mt-4 border-t pt-3'>
              {renderSurchargeRow(
                'addressCorrection',
                'ADDRESS CORRECTION',
                false
              )}
              {renderSurchargeRow('clearanceCharge', 'CLEARANCE CHARGE', false)}
              {renderSurchargeRow(
                'elevatedRiskSurcharge',
                'ELEVATED RISK SURCHARGE'
              )}
              {renderSurchargeRow('ess', 'ESS')}
              {renderSurchargeRow('imitation', 'IMITATION')}
              {renderSurchargeRow(
                'medicineHandlingCharges',
                'MEDICINE HANDLING CHARGES',
                false
              )}
              {renderSurchargeRow(
                'nonStackablePallet',
                'NON STACKABLE PALLET',
                false
              )}
              {renderSurchargeRow(
                'oversizeOverweightSurcharge',
                'OVERSIZE OVERWEIGHT SURCHARGE'
              )}
              {renderSurchargeRow(
                'remoteAreaCharges',
                'REMOTE AREA CHARGES',
                false
              )}
              {renderSurchargeRow(
                'restrictedDestinationSurcharge',
                'RESTRICTED DESTINATION SURCHARGE'
              )}
            </div>

            {/* Total Other Charges & Adjustment */}
            <div className='grid grid-cols-2 gap-2 mt-4 border-t pt-3'>
              <FormField label='TOTAL OTHER CHARGES'>
                <Input
                  type='number'
                  step='0.01'
                  {...register('salesBilling.totalOtherCharges', {
                    valueAsNumber: true,
                  })}
                  className='h-8 text-xs'
                  placeholder='0'
                />
              </FormField>
              <FormField label='ADJUSTMENT AMOUNT'>
                <Input
                  type='number'
                  step='0.01'
                  {...register('salesBilling.adjustmentAmount', {
                    valueAsNumber: true,
                  })}
                  className='h-8 text-xs'
                  placeholder='0'
                />
              </FormField>
            </div>

            {/* FSC % */}
            <div className='grid grid-cols-3 gap-2 mt-3'>
              <FormField label='FSC %'>
                <Input
                  type='number'
                  step='0.01'
                  {...register('salesBilling.fscPercent', {
                    valueAsNumber: true,
                  })}
                  className='h-8 text-xs'
                  placeholder='0'
                />
              </FormField>
              <FormField label=''>
                <Input
                  type='number'
                  step='0.01'
                  {...register('salesBilling.fscPercentValue', {
                    valueAsNumber: true,
                  })}
                  className='h-8 text-xs mt-5'
                  placeholder='0'
                />
              </FormField>
              <div className='flex items-end mb-1'>
                <Controller
                  name='salesBilling.fscPercentEditable'
                  control={control}
                  render={({ field }) => (
                    <label className='flex items-center gap-1 text-xs'>
                      <input
                        type='checkbox'
                        checked={field.value}
                        onChange={field.onChange}
                        className='h-3 w-3'
                      />
                      EDIT FSC %
                    </label>
                  )}
                />
              </div>
            </div>

            {/* FSC */}
            <div className='grid grid-cols-3 gap-2'>
              <FormField label='FSC'>
                <Input
                  type='number'
                  step='0.01'
                  {...register('salesBilling.fsc', { valueAsNumber: true })}
                  className='h-8 text-xs'
                  placeholder='0'
                />
              </FormField>
              <div></div>
              <div className='flex items-end mb-1'>
                <Controller
                  name='salesBilling.fscEditable'
                  control={control}
                  render={({ field }) => (
                    <label className='flex items-center gap-1 text-xs'>
                      <input
                        type='checkbox'
                        checked={field.value}
                        onChange={field.onChange}
                        className='h-3 w-3'
                      />
                      EDIT FSC
                    </label>
                  )}
                />
              </div>
            </div>

            {/* Discount */}
            <div className='grid grid-cols-2 gap-2 mt-3'>
              <FormField label='DISCOUNT (IN %)'>
                <Input
                  type='number'
                  step='0.01'
                  {...register('salesBilling.discountPercent', {
                    valueAsNumber: true,
                  })}
                  className='h-8 text-xs'
                  placeholder='0'
                />
              </FormField>
              <FormField label='DISCOUNT AMOUNT'>
                <Input
                  type='number'
                  step='0.01'
                  {...register('salesBilling.discountAmount', {
                    valueAsNumber: true,
                  })}
                  className='h-8 text-xs'
                  placeholder='0'
                />
              </FormField>
            </div>
            {(watch('salesBilling.totalDiscount') || 0) >
              (watch('salesBilling.freight') || 0) && (
              <div className='text-xs text-amber-600 bg-amber-50 border border-amber-200 p-2 rounded mt-2'>
                ⚠️ Total discount (₹
                {(watch('salesBilling.totalDiscount') || 0).toFixed(2)}) exceeds
                freight amount (₹
                {(watch('salesBilling.freight') || 0).toFixed(2)})
              </div>
            )}

            {/* Calculated Totals */}
            <div className='mt-4 border-t pt-3 space-y-2'>
              <div className='grid grid-cols-2 gap-2 items-center'>
                <span className='text-xs text-gray-600 text-right'>
                  SUBTOTAL
                </span>
                <Input
                  value={watch('salesBilling.subtotal') || 0}
                  disabled
                  className='h-7 text-xs w-28 bg-gray-50'
                />
              </div>
              <div className='grid grid-cols-2 gap-2 items-center'>
                <span className='text-xs text-gray-600 text-right'>
                  TAXABLE AMOUNT
                </span>
                <Input
                  value={watch('salesBilling.taxableAmount') || 0}
                  disabled
                  className='h-7 text-xs w-28 bg-gray-50'
                />
              </div>
              <div className='grid grid-cols-2 gap-2 items-center'>
                <span className='text-xs text-gray-600 text-right'>
                  GST (IN %)
                </span>
                <Input
                  type='number'
                  step='0.01'
                  {...register('salesBilling.gstPercent', {
                    valueAsNumber: true,
                  })}
                  className='h-7 text-xs w-28'
                  placeholder='18'
                />
              </div>
              <div className='grid grid-cols-2 gap-2 items-center'>
                <span className='text-xs text-gray-600 text-right'>CGST</span>
                <Input
                  value={watch('salesBilling.cgst') || 0}
                  disabled
                  className='h-7 text-xs w-28 bg-gray-50'
                />
              </div>
              <div className='grid grid-cols-2 gap-2 items-center'>
                <span className='text-xs text-gray-600 text-right'>SGST</span>
                <Input
                  value={watch('salesBilling.sgst') || 0}
                  disabled
                  className='h-7 text-xs w-28 bg-gray-50'
                />
              </div>
              <div className='grid grid-cols-2 gap-2 items-center'>
                <span className='text-xs text-gray-700 font-semibold text-right'>
                  GRAND TOTAL
                </span>
                <Input
                  type='number'
                  step='0.01'
                  {...register('salesBilling.grandTotal', {
                    valueAsNumber: true,
                  })}
                  className='h-7 text-xs w-28 bg-green-50 font-semibold'
                />
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: Weight Summary */}
        <div className='lg:col-span-3 p-4'>
          <div className='border border-gray-300 dark:border-gray-600 p-4'>
            <div className='space-y-3'>
              <FormField label='PCS'>
                <Input
                  type='number'
                  value={watch('totalPieces') || 0}
                  disabled
                  className='h-8 text-xs bg-gray-50'
                />
              </FormField>
              <FormField label='ACTUAL WEIGHT'>
                <Input
                  type='number'
                  value={watch('totalActualWeight') || 0}
                  disabled
                  className='h-8 text-xs bg-gray-50'
                />
              </FormField>
              <FormField label='VOLUMETRIC WEIGHT'>
                <Input
                  type='number'
                  value={watch('totalVolumetricWeight') || 0}
                  disabled
                  className='h-8 text-xs bg-gray-50'
                />
              </FormField>
              <FormField label='CHARGEABLE WEIGHT'>
                <Input
                  type='number'
                  value={watch('totalChargeableWeight') || 0}
                  disabled
                  className='h-8 text-xs bg-gray-50'
                />
              </FormField>
            </div>

            {/* Invoice Details */}
            <div className='mt-4 border-t pt-3 space-y-2'>
              <FormField label='SALES INVOICE NUMBER'>
                <Input
                  {...register('salesBilling.salesInvoiceNumber')}
                  className='h-8 text-xs'
                />
              </FormField>
              <FormField label='SALES INVOICE DATE'>
                <Input
                  type='date'
                  {...register('salesBilling.salesInvoiceDate')}
                  className='h-8 text-xs'
                />
              </FormField>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ============================================================================
  // RENDER: PURCHASE BILLING TAB
  // ============================================================================

  const renderPurchaseBillingTab = () => (
    <div className='bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'>
      {/* Header */}
      <div className='flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700'>
        <h2 className='text-sm font-semibold text-gray-800 dark:text-gray-200'>
          DOCKET PURCHASE BILLING
        </h2>
        <div className='flex items-center gap-2'>
          <Button
            type='button'
            size='sm'
            onClick={handleSavePurchaseBilling}
            disabled={isSavingPurchaseBilling}
            className='bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-4'
          >
            {isSavingPurchaseBilling ? (
              <RefreshCw className='h-3 w-3 animate-spin mr-1' />
            ) : (
              <Save className='h-3 w-3 mr-1' />
            )}
            {isSavingPurchaseBilling ? 'SAVING...' : 'SAVE BILLING'}
          </Button>
        </div>
      </div>

      {/* Main Content - 2 Column Layout */}
      <div className='grid grid-cols-1 lg:grid-cols-12 gap-0'>
        {/* LEFT COLUMN: Purchase Billing Form */}
        <div className='lg:col-span-5 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700 p-4'>
          <div className='border border-gray-300 dark:border-gray-600 p-4'>
            <h3 className='text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4'>
              PURCHASE BILLING
            </h3>

            {/* Company */}
            <FormField label='COMPANY'>
              <Controller
                name='purchaseBilling.company'
                control={control}
                render={({ field }) => (
                  <SearchableSelect
                    value={field.value || watch('company')}
                    onValueChange={field.onChange}
                    placeholder='Select...'
                    options={masterData.companies.map(c => ({
                      value: c._id,
                      label: c.name,
                    }))}
                    className='h-8 text-xs'
                  />
                )}
              />
            </FormField>

            {/* Currency & GST Type */}
            <div className='grid grid-cols-2 gap-4 mt-3'>
              <FormField label='CURRENCY'>
                <Controller
                  name='purchaseBilling.currency'
                  control={control}
                  render={({ field }) => (
                    <AsyncSearchableSelect
                      loadOptions={fetchCurrencies}
                      defaultOptions={masterData.currencies.map(c => ({
                        value: c._id,
                        label: `${c.symbol ? `${c.symbol} ` : ''}${c.name} (${c.code})`,
                      }))}
                      value={field.value || watch('currency')}
                      onValueChange={field.onChange}
                      placeholder='Select...'
                      className='h-8 text-xs'
                      initialLabel={
                        masterData.currencies.find(
                          c => c._id === (field.value || watch('currency'))
                        )?.code
                      }
                    />
                  )}
                />
              </FormField>
              <FormField label='GST TYPE'>
                <Controller
                  name='purchaseBilling.gstType'
                  control={control}
                  render={({ field }) => (
                    <SearchableSelect
                      value={field.value}
                      onValueChange={field.onChange}
                      placeholder='GST'
                      options={GST_TYPES}
                      className='h-8 text-xs'
                    />
                  )}
                />
              </FormField>
            </div>

            {/* GST Applicable */}
            <div className='flex items-center gap-4 mt-3 mb-4'>
              <Controller
                name='purchaseBilling.gstApplicable'
                control={control}
                render={({ field }) => (
                  <label className='flex items-center gap-2 text-xs'>
                    <input
                      type='checkbox'
                      checked={field.value}
                      onChange={field.onChange}
                      className='h-4 w-4'
                    />
                    GST APPLICABLE
                  </label>
                )}
              />
            </div>

            {/* Freight */}
            <div className='grid grid-cols-2 gap-2 mb-3'>
              <FormField label='FREIGHT'>
                <Input
                  type='number'
                  step='0.01'
                  {...register('purchaseBilling.freight', {
                    valueAsNumber: true,
                  })}
                  className='h-8 text-xs'
                  placeholder='0'
                />
              </FormField>
              <FormField label='FREIGHT PER KG'>
                <Input
                  type='number'
                  step='0.01'
                  {...register('purchaseBilling.freightPerKg', {
                    valueAsNumber: true,
                  })}
                  className='h-8 text-xs'
                  placeholder='0'
                />
              </FormField>
            </div>

            {/* Surcharges */}
            <div className='space-y-1 mt-4 border-t pt-3'>
              {renderPurchaseSurchargeRow(
                'addressCorrection',
                'ADDRESS CORRECTION',
                false
              )}
              {renderPurchaseSurchargeRow(
                'clearanceCharge',
                'CLEARANCE CHARGE',
                false
              )}
              {renderPurchaseSurchargeRow(
                'elevatedRiskSurcharge',
                'ELEVATED RISK SURCHARGE'
              )}
              {renderPurchaseSurchargeRow('ess', 'ESS')}
              {renderPurchaseSurchargeRow('imitation', 'IMITATION')}
              {renderPurchaseSurchargeRow(
                'medicineHandlingCharges',
                'MEDICINE HANDLING CHARGES',
                false
              )}
              {renderPurchaseSurchargeRow(
                'nonStackablePallet',
                'NON STACKABLE PALLET',
                false
              )}
              {renderPurchaseSurchargeRow(
                'oversizeOverweightSurcharge',
                'OVERSIZE OVERWEIGHT SURCHARGE'
              )}
              {renderPurchaseSurchargeRow(
                'remoteAreaCharges',
                'REMOTE AREA CHARGES',
                false
              )}
              {renderPurchaseSurchargeRow(
                'restrictedDestinationSurcharge',
                'RESTRICTED DESTINATION SURCHARGE'
              )}
            </div>

            {/* Total Other Charges */}
            <div className='mt-4 border-t pt-3 space-y-2'>
              <div className='grid grid-cols-2 gap-2 items-center'>
                <span className='text-xs text-gray-600 text-right'>
                  TOTAL OTHER CHARGES
                </span>
                <Input
                  type='number'
                  step='0.01'
                  {...register('purchaseBilling.totalOtherCharges', {
                    valueAsNumber: true,
                  })}
                  className='h-7 text-xs w-28'
                  placeholder='0'
                />
              </div>

              {/* FSC */}
              <div className='grid grid-cols-2 gap-2 items-center'>
                <span className='text-xs text-gray-600 text-right'>FSC %</span>
                <Input
                  type='number'
                  step='0.01'
                  {...register('purchaseBilling.fscPercent', {
                    valueAsNumber: true,
                  })}
                  className='h-7 text-xs w-28'
                  placeholder='0'
                />
              </div>
              <div className='grid grid-cols-2 gap-2 items-center'>
                <span className='text-xs text-gray-600 text-right'>FSC</span>
                <Input
                  type='number'
                  step='0.01'
                  {...register('purchaseBilling.fsc', { valueAsNumber: true })}
                  className='h-7 text-xs w-28'
                  placeholder='0'
                />
              </div>

              {/* Discount */}
              <div className='grid grid-cols-2 gap-2 items-center'>
                <span className='text-xs text-gray-600 text-right'>
                  DISCOUNT (IN %)
                </span>
                <Input
                  type='number'
                  step='0.01'
                  {...register('purchaseBilling.discountPercent', {
                    valueAsNumber: true,
                  })}
                  className='h-7 text-xs w-28'
                  placeholder='0'
                />
              </div>
              <div className='grid grid-cols-2 gap-2 items-center'>
                <span className='text-xs text-gray-600 text-right'>
                  DISCOUNT AMOUNT
                </span>
                <Input
                  type='number'
                  step='0.01'
                  {...register('purchaseBilling.discountAmount', {
                    valueAsNumber: true,
                  })}
                  className='h-7 text-xs w-28'
                  placeholder='0'
                />
              </div>

              {/* Totals */}
              <div className='grid grid-cols-2 gap-2 items-center'>
                <span className='text-xs text-gray-600 text-right'>
                  SUBTOTAL
                </span>
                <Input
                  value={watch('purchaseBilling.subtotal') || 0}
                  disabled
                  className='h-7 text-xs w-28 bg-gray-50'
                />
              </div>
              <div className='grid grid-cols-2 gap-2 items-center'>
                <span className='text-xs text-gray-600 text-right'>
                  GST (IN %)
                </span>
                <Input
                  type='number'
                  step='0.01'
                  {...register('purchaseBilling.gstPercent', {
                    valueAsNumber: true,
                  })}
                  className='h-7 text-xs w-28'
                  placeholder='18'
                />
              </div>
              <div className='grid grid-cols-2 gap-2 items-center'>
                <span className='text-xs text-gray-600 text-right'>CGST</span>
                <Input
                  value={watch('purchaseBilling.cgst') || 0}
                  disabled
                  className='h-7 text-xs w-28 bg-gray-50'
                />
              </div>
              <div className='grid grid-cols-2 gap-2 items-center'>
                <span className='text-xs text-gray-600 text-right'>SGST</span>
                <Input
                  value={watch('purchaseBilling.sgst') || 0}
                  disabled
                  className='h-7 text-xs w-28 bg-gray-50'
                />
              </div>
              <div className='grid grid-cols-2 gap-2 items-center'>
                <span className='text-xs text-gray-700 font-semibold text-right'>
                  GRAND TOTAL
                </span>
                <Input
                  type='number'
                  step='0.01'
                  {...register('purchaseBilling.grandTotal', {
                    valueAsNumber: true,
                  })}
                  className='h-7 text-xs w-28 bg-green-50 font-semibold'
                />
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: Vendor Details + Vendor Invoice */}
        <div className='lg:col-span-7 p-4 space-y-4'>
          {/* Vendor Details Section */}
          <div className='border border-gray-300 dark:border-gray-600 p-4'>
            <h3 className='text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4'>
              VENDOR DETAILS
            </h3>

            <div className='grid grid-cols-2 gap-4'>
              <FormField label='PRODUCT'>
                <Input
                  value={
                    masterData.products.find(p => p._id === watch('product'))
                      ?.name || ''
                  }
                  disabled
                  className='h-8 text-xs bg-gray-50'
                />
              </FormField>
              <FormField label='SERVICE'>
                <Input
                  value={
                    masterData.services.find(s => s._id === watch('service'))
                      ?.name || ''
                  }
                  disabled
                  className='h-8 text-xs bg-gray-50'
                />
              </FormField>
              <FormField label='VENDOR'>
                <Input
                  value={
                    masterData.vendors.find(v => v._id === watch('vendor'))
                      ?.name || ''
                  }
                  disabled
                  className='h-8 text-xs bg-gray-50'
                />
              </FormField>
              <FormField label='PCS'>
                <Input
                  value={watch('totalPieces') || 0}
                  disabled
                  className='h-8 text-xs bg-gray-50'
                />
              </FormField>
            </div>

            {/* Weight Details */}
            <div className='mt-4 grid grid-cols-3 gap-4'>
              <FormField label='ACTUAL WEIGHT'>
                <Input
                  value={watch('totalActualWeight') || 0}
                  disabled
                  className='h-8 text-xs bg-gray-50'
                />
              </FormField>
              <FormField label='VOLUME WEIGHT'>
                <Input
                  value={(watch('totalVolumetricWeight') || 0).toFixed(2)}
                  disabled
                  className='h-8 text-xs bg-gray-50'
                />
              </FormField>
              <FormField label='CHARGEABLE WEIGHT'>
                <Input
                  value={watch('totalChargeableWeight') || 0}
                  disabled
                  className='h-8 text-xs bg-gray-50'
                />
              </FormField>
            </div>

            {/* Contract Details */}
            <div className='mt-4 grid grid-cols-2 gap-4'>
              <FormField label='CFT CONTRACT'>
                <div className='flex gap-2'>
                  <Input
                    {...register('purchaseBilling.cftId')}
                    className='h-8 text-xs w-20'
                    placeholder='CFT ID'
                  />
                  <Input
                    type='number'
                    {...register('purchaseBilling.cftValue', {
                      valueAsNumber: true,
                    })}
                    className='h-8 text-xs'
                    placeholder='CFT Value'
                  />
                </div>
              </FormField>
              <FormField label='VENDOR CONTRACT'>
                <div className='flex gap-2'>
                  <Input
                    {...register('purchaseBilling.vendorContractId')}
                    className='h-8 text-xs'
                    placeholder='Contract ID'
                  />
                  <Input
                    {...register('purchaseBilling.vendorTat')}
                    className='h-8 text-xs w-20'
                    placeholder='TAT'
                  />
                </div>
              </FormField>
            </div>
          </div>

          {/* Vendor Invoice Section */}
          <div className='border border-gray-300 dark:border-gray-600 p-4'>
            <h3 className='text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4'>
              VENDOR INVOICE
            </h3>

            <FormField label='PAST VENDOR INVOICE'>
              <Input
                {...register('purchaseBilling.pastVendorInvoice')}
                className='h-8 text-xs'
                placeholder=''
              />
            </FormField>

            <div className='grid grid-cols-2 gap-4 mt-3'>
              <FormField label='VENDOR INVOICE 1'>
                <Input
                  {...register('purchaseBilling.vendorInvoice1')}
                  className='h-8 text-xs'
                  placeholder=''
                />
              </FormField>
              <FormField label='INVOICE REMARKS 1'>
                <Input
                  {...register('purchaseBilling.invoiceRemarks1')}
                  className='h-8 text-xs'
                  placeholder=''
                />
              </FormField>
            </div>

            <div className='grid grid-cols-2 gap-4 mt-2'>
              <FormField label='VENDOR INVOICE 2'>
                <Input
                  {...register('purchaseBilling.vendorInvoice2')}
                  className='h-8 text-xs'
                  placeholder=''
                />
              </FormField>
              <FormField label='INVOICE REMARKS 2'>
                <Input
                  {...register('purchaseBilling.invoiceRemarks2')}
                  className='h-8 text-xs'
                  placeholder=''
                />
              </FormField>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderExtraTab = () => (
    <div className='space-y-6'>
      {/* Shipment Invoice */}
      <div className='bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-6'>
        <div className='flex items-center gap-2 mb-6'>
          <FileText className='h-5 w-5 text-blue-600' />
          <h2 className='text-lg font-semibold text-gray-800 dark:text-gray-200'>
            Shipment Invoice
          </h2>
        </div>

        <div className='space-y-4'>
          {/* Create Shipment Invoice Checkbox */}
          <div className='flex items-center gap-2'>
            <Controller
              name='createShipmentInvoice'
              control={control}
              render={({ field }) => (
                <input
                  type='checkbox'
                  checked={field.value}
                  onChange={field.onChange}
                  className='h-4 w-4'
                  disabled={isSubmitting}
                />
              )}
            />
            <Label className='text-sm font-medium'>
              Create Shipment Invoice
            </Label>
          </div>

          {/* Always show the invoice form - checkbox controls submission */}
          <div className='border rounded-lg p-4 space-y-4'>
            <div className='border rounded-lg p-4 space-y-4'>
              {/* Invoice Header Row */}
              <div className='grid grid-cols-4 gap-4'>
                <FormField label='INVOICE TYPE'>
                  <Controller
                    name='shipmentInvoice.invoiceType'
                    control={control}
                    render={({ field }) => (
                      <SearchableSelect
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder='Select type'
                        options={[
                          {
                            value: 'COMMERCIAL_INVOICE',
                            label: 'Commercial Invoice',
                          },
                          {
                            value: 'PROFORMA_INVOICE',
                            label: 'Proforma Invoice',
                          },
                        ]}
                        className='h-8 text-xs'
                        disabled={isSubmitting}
                      />
                    )}
                  />
                </FormField>
                <FormField label='CURRENCY'>
                  <Controller
                    name='shipmentInvoice.currency'
                    control={control}
                    render={({ field }) => (
                      <SearchableSelect
                        value={field.value || watch('currency')}
                        onValueChange={field.onChange}
                        placeholder='Select'
                        options={masterData.currencies.map(c => ({
                          value: c._id,
                          label: `${c.symbol || ''} ${c.code}`.trim(),
                        }))}
                        className='h-8 text-xs'
                        disabled={isSubmitting}
                      />
                    )}
                  />
                </FormField>
                <FormField label='INCOTERMS'>
                  <Controller
                    name='shipmentInvoice.incoterms'
                    control={control}
                    render={({ field }) => (
                      <SearchableSelect
                        value={field.value}
                        onValueChange={field.onChange}
                        options={masterData.incoterms.map(i => ({
                          value: i.code,
                          label: i.code,
                        }))}
                        className='h-8 text-xs'
                        disabled={isSubmitting}
                      />
                    )}
                  />
                </FormField>
                <FormField label='NOTE'>
                  <Controller
                    name='shipmentInvoice.note'
                    control={control}
                    render={({ field }) => (
                      <SearchableSelect
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder='Select note'
                        options={NOTE_OPTIONS}
                        className='h-8 text-xs'
                        disabled={isSubmitting}
                      />
                    )}
                  />
                </FormField>
              </div>

              {/* Remarks */}
              <FormField label='REMARKS'>
                <Textarea
                  {...register('shipmentInvoice.remarks')}
                  rows={2}
                  className='text-xs'
                  disabled={isSubmitting}
                  placeholder='Additional remarks for the invoice'
                />
              </FormField>

              {/* Line Items */}
              <div className='space-y-2'>
                <Label className='text-sm font-medium'>Line Items</Label>
                <div className='border rounded-lg overflow-x-auto'>
                  <table className='w-full text-xs'>
                    <thead className='bg-gray-50 dark:bg-gray-700'>
                      <tr>
                        <th className='px-2 py-2 text-left'>Sr.No</th>
                        <th className='px-2 py-2 text-left'>Box No</th>
                        <th className='px-2 py-2 text-left'>Description</th>
                        <th className='px-2 py-2 text-left'>HS Code</th>
                        <th className='px-2 py-2 text-left'>Unit</th>
                        <th className='px-2 py-2 text-left'>Qty</th>
                        <th className='px-2 py-2 text-left'>Unit Wt</th>
                        <th className='px-2 py-2 text-left'>Unit Rate</th>
                        <th className='px-2 py-2 text-left'>Amount</th>
                        <th className='px-2 py-2 text-left'>IGST%</th>
                        <th className='px-2 py-2 text-left'></th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceFields.map((field, index) => (
                        <tr key={field.id} className='border-t'>
                          <td className='px-2 py-2'>
                            <Input
                              type='number'
                              {...register(
                                `shipmentInvoice.lineItems.${index}.srNo` as const,
                                { valueAsNumber: true }
                              )}
                              className='h-7 w-12 text-xs text-center'
                              disabled={isSubmitting}
                            />
                          </td>
                          <td className='px-2 py-2'>
                            <Input
                              {...register(
                                `shipmentInvoice.lineItems.${index}.boxNo` as const
                              )}
                              className='h-7 w-14 text-xs'
                              disabled={isSubmitting}
                            />
                          </td>
                          <td className='px-2 py-2'>
                            <Input
                              {...register(
                                `shipmentInvoice.lineItems.${index}.description` as const
                              )}
                              className='h-7 w-32 text-xs'
                              disabled={isSubmitting}
                            />
                          </td>
                          <td className='px-2 py-2'>
                            <Input
                              {...register(
                                `shipmentInvoice.lineItems.${index}.hsCode` as const
                              )}
                              className='h-7 w-16 text-xs'
                              disabled={isSubmitting}
                            />
                          </td>
                          <td className='px-2 py-2'>
                            <Input
                              {...register(
                                `shipmentInvoice.lineItems.${index}.unitType` as const
                              )}
                              className='h-7 w-14 text-xs'
                              disabled={isSubmitting}
                            />
                          </td>
                          <td className='px-2 py-2'>
                            <Input
                              type='number'
                              step='0.01'
                              {...register(
                                `shipmentInvoice.lineItems.${index}.quantity` as const,
                                { valueAsNumber: true }
                              )}
                              className='h-7 w-16 text-xs'
                              disabled={isSubmitting}
                            />
                          </td>
                          <td className='px-2 py-2'>
                            <Input
                              type='number'
                              step='0.01'
                              {...register(
                                `shipmentInvoice.lineItems.${index}.unitWeight` as const,
                                { valueAsNumber: true }
                              )}
                              className='h-7 w-16 text-xs'
                              disabled={isSubmitting}
                            />
                          </td>
                          <td className='px-2 py-2'>
                            <Input
                              type='number'
                              step='0.01'
                              {...register(
                                `shipmentInvoice.lineItems.${index}.unitRate` as const,
                                { valueAsNumber: true }
                              )}
                              className='h-7 w-18 text-xs'
                              disabled={isSubmitting}
                            />
                          </td>
                          <td className='px-2 py-2'>
                            <Input
                              type='number'
                              step='0.01'
                              {...register(
                                `shipmentInvoice.lineItems.${index}.amount` as const,
                                { valueAsNumber: true }
                              )}
                              className='h-7 w-18 text-xs bg-gray-50'
                              disabled
                            />
                          </td>
                          <td className='px-2 py-2'>
                            <Input
                              type='number'
                              step='0.01'
                              {...register(
                                `shipmentInvoice.lineItems.${index}.igst` as const,
                                { valueAsNumber: true }
                              )}
                              className='h-7 w-14 text-xs'
                              disabled={isSubmitting}
                            />
                          </td>
                          <td className='px-2 py-2'>
                            {invoiceFields.length > 1 && (
                              <Button
                                type='button'
                                variant='ghost'
                                size='sm'
                                onClick={() => removeInvoiceItem(index)}
                                className='h-7 w-7 p-0 text-red-500'
                                disabled={isSubmitting}
                              >
                                <Trash2 className='h-3 w-3' />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={() =>
                    appendInvoiceItem({
                      srNo: invoiceFields.length + 1,
                      boxNo: '',
                      description: '',
                      hsCode: '',
                      unitType: 'PCS',
                      quantity: 1,
                      unitWeight: 0,
                      igst: 18,
                      unitRate: 0,
                      amount: 0,
                    })
                  }
                  className='w-full text-xs'
                  disabled={isSubmitting}
                >
                  <Plus className='h-3 w-3 mr-1' />
                  Add Line Item
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Attachments */}
      <div className='bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-6'>
        <div className='flex items-center gap-2 mb-6'>
          <Upload className='h-5 w-5 text-purple-600' />
          <h2 className='text-lg font-semibold text-gray-800 dark:text-gray-200'>
            Attachments
          </h2>
        </div>
        <AttachmentUploader
          onFilesChange={files => {}}
          maxFiles={10}
          maxFileSize={10 * 1024 * 1024}
          acceptedTypes={['image/*', '.pdf', '.doc', '.docx', '.xls', '.xlsx']}
          disabled={isSubmitting}
          awbNumber={awbNumber || ''}
          loadExistingDocuments={true}
        />
      </div>

      {/* Comments */}
      <div className='bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-6'>
        <div className='flex items-center gap-2 mb-6'>
          <FileText className='h-5 w-5 text-gray-600' />
          <h2 className='text-lg font-semibold text-gray-800 dark:text-gray-200'>
            Comments
          </h2>
        </div>
        <Textarea
          placeholder='Add internal comments or notes...'
          rows={4}
          disabled={isSubmitting}
        />
      </div>
    </div>
  );

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <div className='min-h-screen bg-gray-100 dark:bg-gray-900'>
      {/* Header */}
      <div className='sticky top-0 z-30 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 shadow-sm'>
        <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
          <div className='flex items-center gap-3'>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => navigate('/awb')}
              className='p-1 h-8 w-8'
            >
              <ChevronLeft className='h-5 w-5' />
            </Button>
            <div>
              <h1 className='text-lg font-bold text-gray-800 dark:text-gray-200'>
                Create New AWB
              </h1>
              <p className='text-xs text-gray-500'>
                Fill in the details to create a new Air Waybill
              </p>
            </div>
          </div>
          <div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end'>
            {validationErrors.length > 0 && (
              <Button
                type='button'
                variant='ghost'
                size='sm'
                onClick={handleViewErrors}
                className='h-8 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 justify-start sm:justify-center'
              >
                <AlertCircle className='h-3 w-3 mr-1' />
                {validationErrors.length} issue{validationErrors.length === 1 ? '' : 's'}
              </Button>
            )}
            <div className='flex flex-col gap-2 sm:flex-row sm:items-center w-full sm:w-auto'>
              <Button
                variant='outline'
                size='sm'
                onClick={() => navigate('/awb')}
                className='text-xs w-full sm:w-auto'
              >
                Cancel
              </Button>
              <Button
                size='sm'
                onClick={handleSubmit(onSubmit)}
                disabled={isSubmitting}
                className='text-xs bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto'
              >
                <Save className='h-3 w-3 mr-1' />
                {isSubmitting ? 'Creating...' : 'Create AWB'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <form onSubmit={handleSubmit(onSubmit)} className='p-4'>
        {renderTabs()}
        <div className='mt-0'>
          {activeTab === 'awb' && renderAWBDetailsSection()}
          {activeTab === 'sales' && renderSalesBillingTab()}
          {activeTab === 'purchase' && renderPurchaseBillingTab()}
          {/* {activeTab === 'extra' && renderExtraTab()} */}
        </div>
      </form>
    </div>
  );
};

export default AWBCreatePageNew;
